import { useAuth } from '@/context/AuthContext';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '@/styles/ActionItemStyles.scss';
import '@/styles/ITDiagnosticsStyles.scss';
import { FaCog, FaRegDotCircle, FaExclamationCircle, FaAddressBook, FaBuilding, FaTasks, FaUsers, FaLevelUpAlt, FaTrashAlt, FaEdit, FaCheck, FaCheckCircle, FaTimes, FaCodeBranch, FaPaperclip, FaUserMinus, FaArrowDown, FaHistory, FaCommentDots, FaDesktop, FaNetworkWired, FaLaptop, FaLock, FaSync, FaEnvelope, FaBolt } from 'react-icons/fa';
import { HiOutlineDocumentAdd } from 'react-icons/hi';
import { MdPersonAddAlt1 } from 'react-icons/md';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import useActionItemSignalR from '@/hooks/useActionItemSignalR';

// Import extracted components
import AttachmentsSection from './components/AttachmentsSection';
import CommentInputSection from './components/CommentInputSection';
import ActivityTimeline from './components/ActivityTimeline';
import StatusPrioritySection from './components/StatusPrioritySection';
import ActionItemBoardSelector from './components/ActionItemBoardSelector';
import EndpointDiagnosticsTab from './components/EndpointDiagnosticsTab';
import NetworkDiagnosticsTab from './components/NetworkDiagnosticsTab';
import ActionItemAutomationsTab from './components/ActionItemAutomationsTab';
import PdfAnnotationModal from '@/components/PdfAnnotationModal';
import SlaTrackingWidget from './components/SlaTrackingWidget';
import CsatSurveyModal from './components/CsatSurveyModal';
import MergedCommentsSection from './components/MergedCommentsSection';
import ActionItemTasksTab from './components/ActionItemTasksTab';
import CommentAttachmentLink, { CommentAttachmentImage } from './components/CommentAttachmentLink';
import * as Sentry from '@sentry/react';

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

/**
 * Delay (ms) before revoking a Blob object URL that was created for a
 * programmatic file download.  A short delay gives the browser time to
 * initiate the download before the underlying object URL is released.
 */
const DOWNLOAD_URL_CLEANUP_DELAY_MS = 10000;

/**
 * Returns true if the given ActionItemsType / templateKey should be treated as an
 * IT ticket for the purposes of showing the Endpoint + Network diagnostics tabs,
 * SLA widget and CSAT prompt.
 *
 * IT ticket types are now managed dynamically in the database (see ItTicketTypesPage).
 * This helper uses pattern matching to detect IT types without requiring an API call,
 * which keeps it usable at module scope and in render paths.
 *
 * Matches:
 *   1. Any value starting with "IT-" (custom templates like "IT-Support-Ticket",
 *      "IT-HardwareRequest", etc.).
 *   2. Any value starting with "IT " (e.g. "IT Support Request", "IT Incident").
 *   3. Any value containing "IT Support", "IT Service" or "IT Incident".
 *
 * The backend SlaInitializationService.IsItTicket() uses the same pattern rules
 * combined with a DB-cached set, so frontend and backend always agree.
 */
const isItTicketType = (type) => {
    if (!type) return false;
    const lower = type.toLowerCase();
    if (lower.startsWith('it-')) return true;
    if (lower.startsWith('it ')) return true;
    return lower.includes('it support') || lower.includes('it service') || lower.includes('it incident');
};

/**
 * Action types that appear ONLY in the Comments tab and are excluded from the History tab.
 * These are purely conversational entries (human comments, merged comment copies, and
 * the RouterAlertUpdate progress note).
 */
const COMMENT_ONLY_ACTION_TYPES = new Set(['Comment', 'MergedComment', 'RouterAlertUpdate', 'InternalNote']);

/**
 * Action types that appear in BOTH tabs:
 *   • Comments tab — displayed as contextual progress bubbles alongside human notes
 *   • History tab  — displayed with exact timestamps so engineers can audit the full
 *                    outage/recovery timeline without leaving the History tab
 */
const HISTORY_CROSSPOST_ACTION_TYPES = new Set(['RouterAlertOutage', 'RouterAlertResolved']);

/**
 * Union of all action types that belong in the Comments tab.
 * Computed once at module level so it is not recreated on every render.
 */
const COMMENT_ACTION_TYPES = new Set([...COMMENT_ONLY_ACTION_TYPES, ...HISTORY_CROSSPOST_ACTION_TYPES]);

/**
 * Placeholder SVG data URI displayed in place of unresolved cid: inline image references.
 * Emails processed before inline image resolution was added (or where attachment fetch
 * failed) retain raw cid:xxx references that browsers cannot resolve. This SVG provides
 * a clear, non-intrusive placeholder indicating the image is unavailable.
 */
const CID_PLACEHOLDER_SVG = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='80' viewBox='0 0 200 80'%3E%3Crect width='200' height='80' fill='%23f5f5f5' rx='4'/%3E%3Ctext x='100' y='35' text-anchor='middle' fill='%23999' font-family='sans-serif' font-size='12'%3EInline image%3C/text%3E%3Ctext x='100' y='52' text-anchor='middle' fill='%23bbb' font-family='sans-serif' font-size='10'%3Enot available%3C/text%3E%3C/svg%3E`;

/**
 * Detects whether a string contains HTML markup (email bodies, etc.).
 * Looks for common HTML tags that indicate structured content.
 */
const containsHtml = (text) => {
    if (!text) return false;
    return /<(div|p|br|table|tr|td|th|span|a|img|h[1-6]|ul|ol|li|pre|blockquote|hr|html|head|body)\b/i.test(text);
};

/**
 * Renders HTML email content inside a sandboxed iframe for safe, isolated display.
 * The iframe prevents style leakage and script execution while preserving
 * the original email formatting. DOMPurify is used for sanitization to properly
 * handle all XSS vectors (event handlers, script injection, etc.) instead of regex.
 */
const SandboxedEmailContent = ({ html }) => {
    const iframeRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(200);

    // Sanitize with DOMPurify and build the srcdoc content
    const srcdoc = useMemo(() => {
        // DOMPurify handles script removal, event handler stripping, javascript: URLs,
        // and all known XSS vectors more reliably than regex patterns.
        // Rely on DOMPurify's built-in defaults for event handler removal (covers all on* attrs).
        const sanitized = DOMPurify.sanitize(html, {
            FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
            ALLOW_DATA_ATTR: false
        });

        // Replace any unresolved cid: image references with a styled placeholder.
        // Emails processed before inline image resolution was implemented (or where
        // attachment fetch failed) may still have cid:xxx references in their HTML.
        // Browsers cannot resolve the cid: protocol, so these images appear broken.
        // We replace them with an inline SVG placeholder that clearly communicates
        // the image is unavailable without breaking the email layout.
        const resolvedHtml = sanitized.replace(
            /(<img\s[^>]*?)src\s*=\s*["']cid:[^"']*["']/gi,
            `$1src="${CID_PLACEHOLDER_SVG}" alt="[Inline image not available]" style="opacity:0.7;"`
        );

        return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data: https:; upgrade-insecure-requests; block-all-mixed-content;"><style>
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    margin: 0;
    padding: 12px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  a { color: #1976d2; text-decoration: underline; }
  img { max-width: 100%; height: auto; }
  img[src^="data:image/svg+xml"][alt*="not available"] {
    border: 1px dashed #ddd;
    border-radius: 4px;
    padding: 2px;
  }
  pre { white-space: pre-wrap; overflow-x: auto; background: #f5f5f5; padding: 8px; border-radius: 4px; }
  table { border-collapse: collapse; max-width: 100%; }
  td, th { padding: 4px 8px; border: 1px solid #ddd; }
  blockquote { margin: 8px 0; padding-left: 12px; border-left: 3px solid #ccc; color: #555; }
</style></head><body>${resolvedHtml}</body></html>`;
    }, [html]);

    // Auto-resize after iframe loads
    const handleLoad = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            const height = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 200;
            setIframeHeight(Math.min(Math.max(height, 100), 800));
        } catch { /* cross-origin safety */ }
    }, []);

    return (
        <iframe
            ref={iframeRef}
            className="email-content-iframe"
            sandbox="allow-same-origin"
            srcDoc={srcdoc}
            onLoad={handleLoad}
            title="Email content"
            style={{ width: '100%', height: `${iframeHeight}px`, border: '1px solid #e0e0e0', borderRadius: '6px' }}
        />
    );
};

/**
 * Renders a description that may contain HTML (email) or plain text.
 * HTML content is displayed in a sandboxed iframe; plain text uses the lightweight formatter.
 */
const renderDescription = (text) => {
    if (!text) return null;
    if (containsHtml(text)) {
        return <SandboxedEmailContent html={text} />;
    }
    return renderFormattedDescription(text);
};

/**
 * Renders a description string with lightweight formatting:
 *   - **text** → <strong>text</strong>
 *   - `code` → <code>code</code>
 *   - [text](url) → <a href={url}>text</a>
 *   - Lines starting with "- " → <li> bullet items
 *   - Lines equal to "---" → <hr />
 *   - Newlines → visual line breaks
 */
const renderFormattedDescription = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let bulletBuffer = [];
    let tableBuffer = [];

    const flushBullets = () => {
        if (bulletBuffer.length > 0) {
            elements.push(
                <ul key={`ul-${elements.length}`} className="formatted-desc-list">
                    {bulletBuffer.map((item, i) => (
                        <li key={i}>{parseInline(item)}</li>
                    ))}
                </ul>
            );
            bulletBuffer = [];
        }
    };

    /**
     * Flush accumulated markdown table rows into a rendered <table>.
     * Skips separator rows (|---|---|) and treats the first row as a header.
     */
    const flushTable = () => {
        if (tableBuffer.length === 0) return;
        const dataRows = tableBuffer.filter(r => !r.match(/^\|[\s-:|]+\|$/));
        if (dataRows.length === 0) { tableBuffer = []; return; }

        const parseRow = (row) =>
            row.split('|').slice(1, -1).map(c => c.trim());

        const headerCells = parseRow(dataRows[0]);
        const bodyRows = dataRows.slice(1).map(parseRow);

        elements.push(
            <table key={`tbl-${elements.length}`} className="formatted-desc-table">
                <thead>
                    <tr>{headerCells.map((c, i) => <th key={i}>{parseInline(c)}</th>)}</tr>
                </thead>
                {bodyRows.length > 0 && (
                    <tbody>
                        {bodyRows.map((cells, ri) => (
                            <tr key={ri}>{cells.map((c, ci) => <td key={ci}>{parseInline(c)}</td>)}</tr>
                        ))}
                    </tbody>
                )}
            </table>
        );
        tableBuffer = [];
    };

    /**
     * Parses inline markdown: **bold**, `code`, and [link](url).
     * Splits the string into segments and renders each appropriately.
     */
    const parseInline = (str) => {
        // Split on bold (**...**), inline code (`...`) using non-greedy match,
        // or markdown links ([text](url)).
        const parts = str.split(/(\*\*.*?\*\*|`.*?`|\[[^\]]+\]\([^)]+\))/g);
        return parts.map((part, i) => {
            const boldMatch = part.match(/^\*\*(.*)\*\*$/);
            if (boldMatch) return <strong key={i}>{boldMatch[1]}</strong>;

            const codeMatch = part.match(/^`([^`]+)`$/);
            if (codeMatch) return <code key={i} className="formatted-desc-code">{codeMatch[1]}</code>;

            const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
            if (linkMatch) {
                const [, linkText, href] = linkMatch;
                // Internal links use React Router navigation via href; external links open in a new tab
                const isInternal = href.startsWith('/');
                return isInternal
                    ? <a key={i} href={href} className="formatted-desc-link">{linkText}</a>
                    : <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="formatted-desc-link">{linkText}</a>;
            }

            return <span key={i}>{part}</span>;
        });
    };

    lines.forEach((line, idx) => {
        const trimmed = line.trim();

        // Markdown table rows start and end with |
        if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
            flushBullets();
            tableBuffer.push(trimmed);
            return;
        }

        // If we were collecting table rows but hit a non-table line, flush the table
        flushTable();

        if (trimmed === '---') {
            // Horizontal rule separator
            flushBullets();
            elements.push(<hr key={`hr-${idx}`} className="formatted-desc-hr" />);
        } else if (trimmed.startsWith('- ')) {
            bulletBuffer.push(trimmed.substring(2));
        } else {
            flushBullets();
            if (trimmed === '') {
                elements.push(<div key={`sp-${idx}`} className="formatted-desc-spacer" />);
            } else {
                elements.push(<div key={`ln-${idx}`} className="formatted-desc-line">{parseInline(trimmed)}</div>);
            }
        }
    });

    flushBullets();
    flushTable();
    return <div className="formatted-description">{elements}</div>;
};

const STATUSES = ['Open', 'On-hold', 'Pending', 'Solved', 'Cancelled', 'Closed'];
const STATUS_COLORS = {
    Open: '#1976d2',
    'On-hold': '#ff9800',
  Pending: '#fbc02d',
    Solved: '#388e3c',
    Cancelled: '#d32f2f',
    Closed: '#6E00B3'
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ActionItemDetailPage = () => {
    // ------------------------------------------------------------------------
    // HOOKS & CONTEXT
    // ------------------------------------------------------------------------
    
    const { apiFetch, user, hasPermission } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const canAccessActionItemsPage = hasPermission('/admin/action-items');
    // Users need IT router management permissions to see Endpoint/Network tabs
    const canAccessItDiagnostics = hasPermission('/it/config-management/routers');

    // ------------------------------------------------------------------------
    // STATE - Core Data
    // ------------------------------------------------------------------------
    
    const [actionItem, setActionItem] = useState(null);
    const [canAddInternalNotes, setCanAddInternalNotes] = useState(false);
    const [properties, setProperties] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [centreManagers, setCentreManagers] = useState([]);
    const [departments, setDepartments] = useState([]); // Departments for assignment dropdown
    const [templates, setTemplates] = useState([]); // Action Item templates for dynamic form rendering
    const [taskValues, setTaskValues] = useState({});


    // ------------------------------------------------------------------------
    // STATE - Editable Fields
    // ------------------------------------------------------------------------
    
    const [priority, setPriority] = useState('');
    const [status, setStatus] = useState('');
    const [tenantContactName, setTenantContactName] = useState('');
    const [tenantContactNumber, setTenantContactNumber] = useState('');
    const [tenantContactEmail, setTenantContactEmail] = useState('');

  // ------------------------------------------------------------------------
    // STATE - UI Controls & User Assignment
    // ------------------------------------------------------------------------
    

    const [showPrioritySelect, setShowPrioritySelect] = useState(false);
    const [showStatusSelect, setShowStatusSelect] = useState(false);
    const [showUserEditor, setShowUserEditor] = useState(false);
    const [showRequesterEditor, setShowRequesterEditor] = useState(false);
    const [requesterSearch, setRequesterSearch] = useState('');
    const [showRequesterSuggestions, setShowRequesterSuggestions] = useState(false);
    const [isSavingRequester, setIsSavingRequester] = useState(false);
    const [showDepartmentEditor, setShowDepartmentEditor] = useState(false);
    const [showPropertyAssignEditor, setShowPropertyAssignEditor] = useState(false);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
    const [userSearch, setUserSearch] = useState('');

    // ------------------------------------------------------------------------
    // STATE - Escalation
    // ------------------------------------------------------------------------
    
    const [showEscalateModal, setShowEscalateModal] = useState(false);
    const [escalationReason, setEscalationReason] = useState('');
    const [isEscalating, setIsEscalating] = useState(false);

    // State for admin delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeletingItem, setIsDeletingItem] = useState(false);

    // State for inline template data editing
    const [isEditingTemplateData, setIsEditingTemplateData] = useState(false);
    const [editingDataJson, setEditingDataJson] = useState({});
    const [isSavingTemplateData, setIsSavingTemplateData] = useState(false);
    const [entraEditSearchResults, setEntraEditSearchResults] = useState({});
    const [entraEditSearchLoading, setEntraEditSearchLoading] = useState({});
    const [entraEditDropdownOpen, setEntraEditDropdownOpen] = useState({});
    const entraEditTimers = useRef({});

    // State for role-selector in edit mode
    const [editRoleOptions, setEditRoleOptions] = useState([]);
    const [editRoleLoading, setEditRoleLoading] = useState(false);

    // State for lookup-selector in edit mode
    const [editLookupResults, setEditLookupResults] = useState({});
    const [editLookupLoading, setEditLookupLoading] = useState({});
    const editLookupTimers = useRef({});

    // State for tenant-selector in edit mode
    const [editTenantResults, setEditTenantResults] = useState({});
    const [editTenantLoading, setEditTenantLoading] = useState({});
    const [editTenantDropdownOpen, setEditTenantDropdownOpen] = useState({});
    const editTenantTimers = useRef({});

    // State for shop-number in edit mode
    const [editShopResults, setEditShopResults] = useState({});
    const [editShopLoading, setEditShopLoading] = useState({});
    const [editShopDropdownOpen, setEditShopDropdownOpen] = useState({});
    const [editShopSearchText, setEditShopSearchText] = useState({});

    // State for inline property editing (admin only)
    const [showPropertyEditor, setShowPropertyEditor] = useState(false);
    const [editingPropertyId, setEditingPropertyId] = useState(null);
    const [isSavingProperty, setIsSavingProperty] = useState(false);

    // ------------------------------------------------------------------------
    // STATE - Action Item Details inline editing
    // Allows authorised users to edit the core details (title, description,
    // priority and property) directly on the detail page without navigating away.
    // Initial Comments are intentionally kept read-only.
    // ------------------------------------------------------------------------

    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [editDetailsTitle, setEditDetailsTitle] = useState('');
    const [editDetailsDescription, setEditDetailsDescription] = useState('');
    const [editDetailsPriority, setEditDetailsPriority] = useState('Low');
    const [editDetailsPropertyId, setEditDetailsPropertyId] = useState('');
    const [isSavingDetails, setIsSavingDetails] = useState(false);

    // ------------------------------------------------------------------------
    // STATE - Email re-fetch
    // For action items created from inbound emails, allows users to re-fetch
    // the original email content from Graph API to resolve inline images that
    // may have failed during initial processing.
    // ------------------------------------------------------------------------

    const [isRefetchingEmail, setIsRefetchingEmail] = useState(false);
    const [refetchResult, setRefetchResult] = useState(null);

    // ------------------------------------------------------------------------
    // STATE - SIPA Report deep-link
    // For action items created from SIPA reports, resolves the report ID and
    // subsection ID so we can link back to the exact question in the SIPA form.
    // ------------------------------------------------------------------------

    const [sipaReportLink, setSipaReportLink] = useState(null); // { reportId, subSectionId }

    // ------------------------------------------------------------------------
    // STATE - Progress tracking
    // The progressPercent field (0-100) is stored on the action item and
    // displayed as a visual progress bar in the sidebar.
    // ------------------------------------------------------------------------

    const [progressValue, setProgressValue] = useState(0);
    const [isEditingProgress, setIsEditingProgress] = useState(false);
    const [isSavingProgress, setIsSavingProgress] = useState(false);

    // ------------------------------------------------------------------------
    // STATE - Tags
    // Tags are stored as a comma-separated string and rendered as removable chips.
    // ------------------------------------------------------------------------

    const [tagsValue, setTagsValue] = useState(''); // internal comma-separated string
    const [newTagInput, setNewTagInput] = useState('');
    const [isSavingTags, setIsSavingTags] = useState(false);

    // ------------------------------------------------------------------------
    // STATE - Active tab in the Comment/History panel
    // 'comments' shows the comment composer and comment log entries.
    // 'history' shows all non-comment action log entries (status changes, etc.)
    // ------------------------------------------------------------------------

    const [activeTab, setActiveTab] = useState('comments');

    // Track the number of applicable automations for this action item.
    // Populated by ActionItemAutomationsTab's onCountChange callback so the
    // tab badge is accurate without a separate fetch from this component.
    const [automationsCount, setAutomationsCount] = useState(0);

    // ------------------------------------------------------------------------
    // STATE - Comments
    // ------------------------------------------------------------------------
    
    const [commentText, setCommentText] = useState('');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);

    // ------------------------------------------------------------------------
    // STATE - File Attachments
    // ------------------------------------------------------------------------
    
    const [attachments, setAttachments] = useState([]);
    const [loadingAttachments, setLoadingAttachments] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [showEscalateCommentModal, setShowEscalateCommentModal] = useState(false);
    const [selectedCommentForEscalation, setSelectedCommentForEscalation] = useState(null);
    const fileInputRef = useRef(null);

    // ------------------------------------------------------------------------
    // STATE - PDF Annotation
    // ------------------------------------------------------------------------
    
    const [showPdfAnnotationModal, setShowPdfAnnotationModal] = useState(false);
    const [selectedPdfAttachment, setSelectedPdfAttachment] = useState(null);

    // ------------------------------------------------------------------------
    // STATE - Remote Connect (Splashtop)
    // ------------------------------------------------------------------------

    const [remoteConnectConfig, setRemoteConnectConfig] = useState(null);
    const [remoteDeviceInfo, setRemoteDeviceInfo] = useState(null);

    // ------------------------------------------------------------------------
    // STATE - IT Diagnostics (Endpoint + Network tabs for IT tickets)
    // ------------------------------------------------------------------------

    const [itDiagnostics, setItDiagnostics] = useState(null);
    const [itDiagnosticsLoading, setItDiagnosticsLoading] = useState(false);

    // ------------------------------------------------------------------------
    // REFS - Click Outside Detection
    // ------------------------------------------------------------------------
    
    const sidebarRef = useRef(null);
    const userAssignRef = useRef(null);
    // Debounce timer ref for task comment saves — checkbox toggles save immediately,
    // but text comment keystrokes debounce to avoid a PUT request per character.
    const taskSaveTimerRef = useRef(null);

    // ------------------------------------------------------------------------
    // UTILITY FUNCTIONS - User Display (defined early for use in computed values)
    // ------------------------------------------------------------------------

    /**
     * Get a display-friendly name for a user object
     * @param {Object} userObj - User object with various name properties
     * @returns {string} Formatted display name
     */
    const getUserDisplay = (userObj) => {
        if (!userObj) return '';
        const first = userObj.firstName || userObj.name || '';
        const last = userObj.lastName || userObj.surname || '';
        const composed = `${first} ${last}`.trim();
        return composed || userObj.displayName || userObj.email || (userObj.userID ?? userObj.id) || '';
    };

    // ------------------------------------------------------------------------
    // COMPUTED VALUES
    // ------------------------------------------------------------------------
    
    const currentUserId = user?.userID || user?.id;

    const propertyName = useMemo(() => {
        if (!actionItem || !properties.length) return '';
        const pid = actionItem.actionItem.propertyID;
        if (!pid) return '';
        const property = properties.find(p => p.propertyID === pid);
        return property?.propertyName ?? 'Unknown Property';
    }, [actionItem, properties]);

    const filteredUsers = useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        if (!term) return allUsers;
        return allUsers.filter(u => getUserDisplay(u).toLowerCase().includes(term));
    }, [allUsers, userSearch]);

    const filteredRequesterUsers = useMemo(() => {
        const term = requesterSearch.trim().toLowerCase();
        if (!term) return allUsers;
        return allUsers.filter(u => getUserDisplay(u).toLowerCase().includes(term));
    }, [allUsers, requesterSearch]);

    /**
     * Parse the comma-separated tags string into a trimmed array of non-empty strings.
     * Used to render tag chips in the sidebar.
     */
    const parsedTags = useMemo(() => {
        if (!tagsValue) return [];
        return tagsValue.split(',').map(t => t.trim()).filter(Boolean);
    }, [tagsValue]);

    /**
     * Detect whether this action item was merged into another.
     * When true, returns an object with the target item ID so a banner can be shown.
     * The merge log entry's NewValue is "Merged into #X" on the source item.
     */
    const mergedInfo = useMemo(() => {
        if (!actionItem?.logs) return null;
        const mergeLog = actionItem.logs.find(
            l => l.actionType === 'Merged' && l.newValue?.startsWith('Merged into #')
        );
        if (!mergeLog) return null;
        return {
            targetId: mergeLog.newValue.replace('Merged into #', ''),
            mergedAt: mergeLog.changedAt,
            mergedByUserId: mergeLog.changedByUserId
        };
    }, [actionItem]);

    /**
     * Split the full log array into:
     *   nativeCommentLogs  — Comment, InternalNote, RouterAlert… entries (NOT MergedComment)
     *                        rendered in the primary comment stream
     *   mergedCommentGroups— MergedComment entries grouped by source item ID, enriched with
     *                        the merge date from the corresponding 'Merged' history log entry;
     *                        rendered in the dedicated MergedCommentsSection below the stream
     *   historyLogs        — every log type that is NOT comment-only (for the History tab)
     *   commentCount       — total count (native + merged) shown on the Comments tab badge
     *
     * RouterAlertOutage / RouterAlertResolved are intentionally in BOTH tabs
     * (see HISTORY_CROSSPOST_ACTION_TYPES for the full rationale).
     */
    const { nativeCommentLogs, mergedCommentGroups, historyLogs, commentCount, historyCount } = useMemo(() => {
        const allLogs = actionItem?.logs ?? [];

        // ── Comments tab: all COMMENT_ACTION_TYPES ──────────────────────────
        const comments = allLogs.filter(l => COMMENT_ACTION_TYPES.has(l.actionType));

        // ── Native comments (excludes MergedComment entries) ─────────────────
        // These are shown in the primary comment stream; merged comments get
        // their own grouped section so they do not break the native thread.
        const nativeComments = comments.filter(l => l.actionType !== 'MergedComment');

        // ── Group MergedComment entries by source item ID (stored in oldValue) ─
        // Each group represents all comments transferred from one merged item.
        const mergedMap = new Map();
        for (const log of allLogs) {
            if (log.actionType !== 'MergedComment') continue;
            const sourceId = String(log.oldValue);
            if (!mergedMap.has(sourceId)) {
                mergedMap.set(sourceId, { sourceItemId: sourceId, comments: [] });
            }
            mergedMap.get(sourceId).comments.push(log);
        }

        // Attach merge metadata from the 'Merged' log entry on the target item.
        // On the TARGET item a Merged log has: oldValue = sourceItemId,
        // newValue = targetItemId as a plain numeric string (NOT "Merged into #X").
        const mergedGroups = Array.from(mergedMap.values()).map(group => {
            const mergeLog = allLogs.find(
                l => l.actionType === 'Merged' &&
                     String(l.oldValue) === group.sourceItemId &&
                     !l.newValue?.startsWith('Merged into #')
            );

            // Sort comments chronologically (oldest-first) for natural conversation flow
            const sortedComments = [...group.comments].sort(
                (a, b) => new Date(a.changedAt) - new Date(b.changedAt)
            );

            return {
                sourceItemId:   group.sourceItemId,
                comments:       sortedComments,
                mergeDate:      mergeLog?.changedAt    ?? null,
                mergedByUserId: mergeLog?.changedByUserId ?? null
            };
        });

        // ── History tab: everything NOT in COMMENT_ONLY_ACTION_TYPES ────────
        // Also filter out entries with no actionType (empty system events that have
        // no meaningful content to display to the user).
        const history = allLogs.filter(l =>
            !COMMENT_ONLY_ACTION_TYPES.has(l.actionType) &&
            l.actionType != null &&
            l.actionType !== ''
        );

        return {
            nativeCommentLogs:   nativeComments,
            mergedCommentGroups: mergedGroups,
            historyLogs:         history,
            commentCount:        comments.length,
            historyCount:        history.length
        };
    }, [actionItem?.logs]);

    /**
     * Fetch the action item details from the API
     */
    const fetchActionItem = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${id}`);
            const data = await res.json();
            if (data.success) {
                const ai = data.data;
                setActionItem(ai);
                // Track whether the current user can add internal notes on this board
                setCanAddInternalNotes(data.canAddInternalNotes ?? false);
                setTenantContactName(ai.actionItem.tenantContactName ?? '');
                setTenantContactNumber(ai.actionItem.tenantContactNumber ?? '');
                setTenantContactEmail(ai.actionItem.tenantContactEmail ?? '');
                // Initialise progress and tags from server data
                setProgressValue(ai.actionItem.progressPercent ?? 0);
                setTagsValue(ai.actionItem.tags ?? '');

                // Clear attention flag for the current user when viewing the item
                // This works for assignees, collaborators, observers, and creators
                // The backend will only clear attention if the user has a pending attention record
                try {
                    await apiFetch(`/api/ActionItems/Settings/${id}/clear-attention?userId=${user.userID}`, {
                        method: 'POST'
                    });
                } catch (clearErr) {
                    Sentry.captureException(clearErr);
                    console.debug('Failed to clear attention flag:', clearErr);
                }
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Fetch action item failed', e);
        }
    }, [apiFetch, id, user]);

    // ------------------------------------------------------------------------
    // EFFECTS - Outside Click Handler
    // ------------------------------------------------------------------------
    
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
         setShowPrioritySelect(false);
     setShowStatusSelect(false);
     setShowUserSuggestions(false);
    }
            if (userAssignRef.current && !userAssignRef.current.contains(e.target)) {
         setShowUserSuggestions(false);
 }
        };

    document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ------------------------------------------------------------------------
    // API CALLS - Fetch Data (declared before mount effect to avoid TDZ)
    // ------------------------------------------------------------------------

    const fetchProperties = async () => {
   try {
       const res = await apiFetch('/api/Property/all');
        const data = await res.json();
            if (data.success) setProperties(data.data);
  } catch (e) {
      Sentry.captureException(e);
       console.error('Error fetching properties', e);
        }
    };

    const fetchAllUsers = async () => {
        try {
      const res = await apiFetch('/api/User/all');
   if (res.ok) {
        const data = await res.json().catch(() => null);
      setAllUsers(data?.data || []);
    }
        } catch (catchError) {
            Sentry.captureException(catchError);
  /* ignore */
        }
    };

    const fetchCentreManagers = async () => {
        try {
      const res = await apiFetch('/api/User/by-role?roleName=Centre%20Manager');
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
          setCentreManagers(data.data);
         } else {
     setCentreManagers([]);
 }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setCentreManagers([]);
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await apiFetch('/api/Department');
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
                setDepartments(data.data);
            } else {
                setDepartments([]);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setDepartments([]);
        }
    };

    const fetchTemplates = async () => {
        try {
            const res = await apiFetch('/api/Templates');
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
                setTemplates(data.data);
            } else {
                setTemplates([]);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setTemplates([]);
        }
    };

    const fetchAttachments = async () => {
        try {
            setLoadingAttachments(true);
            const res = await apiFetch(`/api/ActionItems/action-items/${id}/attachments`);
            const data = await res.json();
            if (data.success && Array.isArray(data.data)) {
                setAttachments(data.data);
            } else {
                setAttachments([]);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setAttachments([]);
        } finally {
            setLoadingAttachments(false);
        }
    };

    // ------------------------------------------------------------------------
    // EFFECTS - Data Fetching on Mount
    // ------------------------------------------------------------------------

    /* eslint-disable react-hooks/set-state-in-effect -- data fetching on mount requires setState */
    useEffect(() => {
        fetchActionItem();
        fetchProperties();
        fetchAllUsers();
        fetchCentreManagers();
        fetchDepartments();
        fetchTemplates();
        fetchAttachments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /* eslint-enable react-hooks/set-state-in-effect */

    // ------------------------------------------------------------------------
    // SIGNALR - Real-time Updates
    // ------------------------------------------------------------------------
    
    const handleActionItemUpdate = useCallback((update) => {
        console.log('[SignalR Detail] Action item updated, refreshing data...', update);
        // Refresh the full action item data to get latest changes (comments, status, etc.)
        fetchActionItem();
    }, [fetchActionItem]);
    
    useActionItemSignalR({
        onActionItemUpdated: handleActionItemUpdate,
        enabled: true,
    });

    // ------------------------------------------------------------------------
    // EFFECTS - Polling for Real-time Updates
    // ------------------------------------------------------------------------
    
    /**
     * Polling effect to automatically refresh action item data.
     * Polls every 30 seconds when the page is visible.
     * Pauses polling when the browser tab is not visible to save resources.
     */
    useEffect(() => {
        const POLLING_INTERVAL = 30000; // 30 seconds
        let pollInterval;
        
        /**
         * Silently fetches action item without showing loading state.
         * Used for background polling to update data.
         */
        const pollActionItem = async () => {
            try {
                const res = await apiFetch(`/api/ActionItems/action-items/${id}`);
                const data = await res.json();
                if (data.success) {
                    const ai = data.data;
                    setActionItem(ai);
                    // Only update contact fields if user is not currently editing them
                    if (!showUserEditor) {
                        setTenantContactName(ai.actionItem.tenantContactName ?? '');
                        setTenantContactNumber(ai.actionItem.tenantContactNumber ?? '');
                        setTenantContactEmail(ai.actionItem.tenantContactEmail ?? '');
                    }
                }
            } catch (e) {
                Sentry.captureException(e);
                // Silently fail on polling errors to avoid spamming the user
                console.debug("Polling error:", e);
            }
        };
        
        /**
         * Handle visibility change - pause polling when tab is hidden.
         */
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden, clear the interval
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                }
            } else {
                // Page is visible again, restart polling
                if (!pollInterval) {
                    pollActionItem(); // Fetch immediately when becoming visible
                    pollInterval = setInterval(pollActionItem, POLLING_INTERVAL);
                }
            }
        };
        
        // Start polling
        pollInterval = setInterval(pollActionItem, POLLING_INTERVAL);
        
        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Cleanup on unmount
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [apiFetch, id, showUserEditor]);

    // ------------------------------------------------------------------------
  // EFFECTS - Sync Status and Priority
    // ------------------------------------------------------------------------
    
    /* eslint-disable react-hooks/set-state-in-effect -- editable fields synced from server */
    useEffect(() => {
        if (actionItem?.actionItem) {
            setStatus(actionItem.actionItem.status || '');
            setPriority(actionItem.actionItem.priority || '');
        }
    }, [actionItem]);
    /* eslint-enable react-hooks/set-state-in-effect */

    // ------------------------------------------------------------------------
    // COMPUTED - Derived Assignments (useMemo instead of useEffect+setState)
    // ------------------------------------------------------------------------

    const assignedUser = useMemo(() => {
        if (!actionItem?.actionItem) return null;
        const assignedUserId = actionItem.actionItem.assignedToUserID;
        if (!assignedUserId) return null;
        return allUsers.find(u => (u.userID ?? u.id) === assignedUserId) || null;
    }, [actionItem, allUsers]);

    const requesterUser = useMemo(() => {
        if (!actionItem?.actionItem) return null;
        const requesterId = actionItem.actionItem.requesterUserID || actionItem.actionItem.userID;
        if (!requesterId) return null;
        return allUsers.find(u => (u.userID ?? u.id) === requesterId) || null;
    }, [actionItem, allUsers]);

    const assignedDepartment = useMemo(() => {
        if (!actionItem?.actionItem) return null;
        const assignedDeptId = actionItem.actionItem.assignedToDepartmentID;
        if (!assignedDeptId) return null;
        return departments.find(d => d.departmentID === assignedDeptId) || null;
    }, [actionItem, departments]);

    const assignedProperty = useMemo(() => {
        if (!actionItem?.actionItem) return null;
        const assignedPropId = actionItem.actionItem.assignedToPropertyID;
        if (!assignedPropId) return null;
        return properties.find(p => p.propertyID === assignedPropId) || null;
    }, [actionItem, properties]);

    // ------------------------------------------------------------------------
    // EFFECTS - Remote Connect Config (Splashtop) — IT Tickets Only
    // ------------------------------------------------------------------------

    useEffect(() => {
        const ai = actionItem?.actionItem;
        if (!ai || !isItTicketType(ai.actionItemsType)) return;

        const fetchRemoteConfig = async () => {
            try {
                const res = await apiFetch('/api/User/remote-connect-config');
                const data = await res.json();
                if (data.success && data.data?.enabled) {
                    setRemoteConnectConfig(data.data);
                }
            } catch { /* non-critical */ }
        };
        fetchRemoteConfig();
    }, [actionItem, apiFetch]);

    useEffect(() => {
        if (!remoteConnectConfig?.enabled) return;
        const ai = actionItem?.actionItem;
        if (!ai) return;

        // Try the requester first (they're the one needing support), then the assigned user's device
        const targetUserId = ai.requesterUserID || ai.userID;
        if (!targetUserId) return;

        const fetchDeviceInfo = async () => {
            try {
                const res = await apiFetch(`/api/User/${targetUserId}/device-info`);
                const data = await res.json();
                if (data.success && data.data?.lastDeviceHostname) {
                    setRemoteDeviceInfo(data.data);
                }
            } catch { /* non-critical */ }
        };
        fetchDeviceInfo();
    }, [remoteConnectConfig, actionItem, apiFetch]);

    // ------------------------------------------------------------------------
    // EFFECTS - IT Diagnostics (Endpoint + Network tabs for IT tickets)
    // Fetches combined diagnostics data from the backend when the action item
    // is an IT ticket type. The backend aggregates Entra/Intune endpoint data
    // and searches MikroTik DHCP leases to locate the user's device.
    // ------------------------------------------------------------------------

    const isItTicket = useMemo(() => {
        const ai = actionItem?.actionItem;
        return ai ? isItTicketType(ai.actionItemsType) : false;
    }, [actionItem]);

    // Endpoint + Network diagnostics tabs require both IT ticket type AND
    // permission to access the router management pages. This prevents the
    // tabs from rendering for users who cannot navigate to those pages.
    const showItDiagnostics = isItTicket && canAccessItDiagnostics;

    useEffect(() => {
        if (!showItDiagnostics || !actionItem?.actionItem?.id) return;

        const fetchDiagnostics = async () => {
            setItDiagnosticsLoading(true);
            try {
                const res = await apiFetch(`/api/ITDiagnostics/action-item/${actionItem.actionItem.id}`);
                const data = await res.json();
                if (data.success) {
                    setItDiagnostics(data);
                }
            } catch (err) {
                Sentry.captureException(err);
                console.debug('IT diagnostics fetch failed:', err);
            } finally {
                setItDiagnosticsLoading(false);
            }
        };
        fetchDiagnostics();
    }, [showItDiagnostics, actionItem?.actionItem?.id, actionItem?.actionItem?.requesterUserID, apiFetch]);

    // ------------------------------------------------------------------------
    // EFFECTS - SIPA Report deep-link resolution
    // For SIPA-sourced action items, extracts or resolves the report ID so we
    // can render a "View SIPA Report" link that navigates to the exact form
    // and scrolls to the relevant subsection question.
    // ------------------------------------------------------------------------

    /* eslint-disable react-hooks/set-state-in-effect -- async SIPA report resolution requires effect */
    useEffect(() => {
        const ai = actionItem?.actionItem;
        if (!ai || ai.source !== 'SipaReportCapture') {
            setSipaReportLink(null);
            return;
        }

        const desc = ai.description || '';
        const subMatch = desc.match(/\bsub:(\d+)/);
        const rptMatch = desc.match(/\brpt:(\d+)/);
        const subSectionId = ai.relatedType === 'SipaSubSection' && ai.relatedId
            ? ai.relatedId
            : subMatch ? parseInt(subMatch[1], 10) : null;

        if (!subSectionId) {
            setSipaReportLink(null);
            return;
        }

        // If rpt: tag is present in description, use it directly
        if (rptMatch) {
            setSipaReportLink({ reportId: parseInt(rptMatch[1], 10), subSectionId });
            return;
        }

        // Fallback: resolve report ID from the backend for older items
        (async () => {
            try {
                const propertyId = ai.propertyID || ai.PropertyID || ai.propertyId;
                if (!propertyId) return;
                const res = await apiFetch(`/api/SipaCompletion/sipa/resolve-report?propertyId=${propertyId}&subSectionId=${subSectionId}`);
                if (!res.ok) return;
                const data = await res.json();
                if (data?.success && data.reportId) {
                    setSipaReportLink({ reportId: data.reportId, subSectionId });
                }
            } catch {
                // Non-critical — link simply won't appear
            }
        })();
    }, [actionItem, apiFetch]);
    /* eslint-enable react-hooks/set-state-in-effect */

    // ------------------------------------------------------------------------
    // API CALLS - Fetch Data
    // ------------------------------------------------------------------------

    



    /**
     * Search Microsoft 365 / Entra ID users by name or email to power the
     * @mention fallback in the comment composer.
     *
     * Called by CommentInputSection when the typed @mention term matches no
     * local CapTrac users. Returns an array of lightweight user objects:
     *   { id, displayName, email, jobTitle, department }
     *
     * Returns an empty array on any error so the mention dropdown degrades
     * gracefully rather than crashing the comment form.
     *
     * @param {string} term - The partial name/email typed after '@'
     * @returns {Promise<Array>} Matching M365 user objects
     */
    const handleMentionSearch = useCallback(async (term) => {
        try {
            const res = await apiFetch(`/api/Entra/users/search?q=${encodeURIComponent(term)}&top=8`);
            if (!res.ok) {
                // Capture non-OK API responses to Sentry for monitoring.
                // Avoid including the search term itself as it may contain email addresses (PII).
                Sentry.captureMessage(`M365 mention search returned HTTP ${res.status}`, 'warning');
                return [];
            }
            const data = await res.json().catch(() => null);
            // Normalise the Entra response to the shape CommentInputSection expects:
            // getUserDisplay reads 'displayName' and 'email' at minimum.
            return (data?.data || []).map(u => ({
                ...u,
                // Entra returns 'email' already, but guard against mail vs email field name
                email: u.email || u.mail || u.userPrincipalName || ''
            }));
        } catch (err) {
            Sentry.captureException(err);
            return [];
        }
    }, [apiFetch]);

    const templateTasks = useMemo(() => {
        if (!actionItem?.actionItem?.templateKey || !templates.length) return [];
        const tpl = templates.find(t => t.templateKey === actionItem.actionItem.templateKey);
        if (!tpl?.schema) return [];
        try {
            const schema = typeof tpl.schema === 'string' ? JSON.parse(tpl.schema) : tpl.schema;
            return schema.tasks || [];
        } catch {
            return [];
        }
    }, [actionItem, templates]);

    // ── Load persisted task values from TaskDataJson when action item loads ──
    useEffect(() => {
        if (!actionItem?.actionItem?.taskDataJson) return;
        try {
            const parsed = typeof actionItem.actionItem.taskDataJson === 'string'
                ? JSON.parse(actionItem.actionItem.taskDataJson)
                : actionItem.actionItem.taskDataJson;
            setTaskValues(parsed || {});
        } catch {
            setTaskValues({});
        }
    }, [actionItem]);

    // ── Persist task values to the backend ──
    /**
     * Sends a single PUT request to save the current task checklist state.
     * Extracted so it can be called both immediately (checkbox toggles) and
     * via a debounce timer (comment keystrokes).
     */
    const saveTaskData = useCallback(async (updatedValues, changeDescription) => {
        try {
            await apiFetch(`/api/ActionItems/action-items/${id}/task-data`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    taskDataJson: JSON.stringify(updatedValues),
                    changeDescription
                })
            });
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to save task data:', err);
        }
    }, [apiFetch, id]);

    /**
     * Handles a single task item change (checkbox toggle or comment update).
     *
     * - Checkbox toggles are discrete, meaningful actions and are saved immediately.
     * - Comment changes debounce for 700 ms to avoid flooding the audit log and the
     *   network with a PUT request on every keystroke.
     *
     * Local state is always updated synchronously for a responsive UI.
     */
    const handleTaskChange = useCallback(async (key, value) => {
        const oldVal = taskValues[key] || { checked: false, comments: '' };
        const updated = { ...taskValues, [key]: value };
        setTaskValues(updated);

        // Build a human-readable description of what changed.
        // key is "groupIdx_itemIdx" — resolve the label from templateTasks.
        const [gIdx, iIdx] = key.split('_').map(Number);
        const group = templateTasks[gIdx];
        const item = group?.items?.[iIdx];
        const itemLabel = item?.label || `Item ${iIdx + 1}`;
        const groupName = group?.name || `Checklist ${gIdx + 1}`;

        const parts = [];
        if (value.checked !== oldVal.checked) {
            parts.push(value.checked ? 'checked' : 'unchecked');
        }
        if ((value.comments || '') !== (oldVal.comments || '')) {
            parts.push(value.comments ? 'comment updated' : 'comment removed');
        }

        const changeDescription = `${groupName} › ${itemLabel}: ${parts.join(', ')}`;
        const isCheckboxChange = value.checked !== oldVal.checked;

        try {
            const response = await apiFetch(`/api/ActionItems/action-items/${id}/task-data`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    taskDataJson: JSON.stringify(updated),
                    userId: user?.userID || user?.id,
                    changeDescription
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchActionItem();
            }
        } catch (err) {
            Sentry.captureException(err);
            console.error('Failed to save task data:', err);
        }
    }, [taskValues, templateTasks, apiFetch, id, user, fetchActionItem]);

    /**
     * Upload a file attachment to the action item
     * Uses the FileStorageService configured in settings (SharePoint or Local)
     * @param {File} file - The file to upload
     * @param {string} description - Optional description for the attachment
     * @returns {object|null} { success, url, fileName, attachmentId, contentType } on success, null on failure.
     *   `url` is the authenticated app download endpoint (Graph-proxied), NOT the raw SharePoint URL,
     *   so readers can open the file locally without needing SharePoint site permissions and the PDF
     *   annotation feature can resolve the attachment from the link.
     */
    const uploadAttachment = async (file, description = '') => {
        if (!actionItem?.actionItem) return null;

        setUploadingFile(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('description', description);
            formData.append('userId', currentUserId);

            // Get auth token from localStorage for authenticated upload
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            const res = await fetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/attachments`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });

            const data = await res.json();
            if (data.success) {
                toast.success('File uploaded successfully');
                fetchAttachments();
                // Always link comment-dropped files to the authenticated app download endpoint
                // (Graph-proxied) rather than the raw SharePoint URL. This lets any user with
                // access to the action item open the file locally without SharePoint permissions,
                // and lets the PDF annotation feature resolve the attachment from the link.
                const attachmentId = data.data?.attachmentId ?? null;
                const downloadUrl = data.data?.downloadUrl
                    || (attachmentId != null ? `/api/ActionItems/attachments/${attachmentId}/download` : '');
                return {
                    success: true,
                    url: downloadUrl,
                    fileName: file.name,
                    attachmentId,
                    contentType: file.type || ''
                };
            } else {
                toast.error(data.message || 'Failed to upload file');
                return null;
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error uploading file:', e);
            toast.error('Error uploading file');
            return null;
        } finally {
            setUploadingFile(false);
        }
    };

    /**
     * Opens a Splashtop remote support session to the requester's last known device.
     * Uses the locally-installed Splashtop app via its custom URI protocol handler.
     */
    const handleRemoteConnect = () => {
        if (!remoteDeviceInfo?.lastDeviceHostname || !remoteConnectConfig) return;

        const protocol = remoteConnectConfig.uriProtocol || 'st-business://';
        const hostname = remoteDeviceInfo.lastDeviceHostname;

        // Attempt to open the Splashtop URI protocol.
        // The OS will hand this off to the locally-installed Splashtop app.
        // If not installed, the browser will typically show a "can't open" dialog.
        window.open(`${protocol}com.splashtop.business?host=${encodeURIComponent(hostname)}`, '_self');

        toast.info(`Launching Splashtop session to ${hostname}...`, { autoClose: 3000 });
    };

    /**
     * Permanently deletes this action item and all its related data (Admin only).
     * Navigates back to the admin action items page on success.
     */
    const handleDeleteActionItem = async () => {
        setIsDeletingItem(true);
        try {
            const response = await apiFetch(`/api/ActionItems/${id}`, { method: 'DELETE' });
            const data = await response.json();
            if (data.success) {
                toast.success('Action item permanently deleted.');
                navigate('/admin/action-items');
            } else {
                toast.error(data.message || 'Failed to delete action item.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while deleting the action item.');
        } finally {
            setIsDeletingItem(false);
            setShowDeleteModal(false);
        }
    };

    /**
     * Begins editing the "Action Item Details" section by seeding the edit
     * state fields with the current saved values.
     */
    const handleStartEditDetails = () => {
        const ai = actionItem?.actionItem;
        if (!ai) return;
        setEditDetailsTitle(ai.title || '');
        setEditDetailsDescription(ai.description || '');
        setEditDetailsPriority(ai.priority || 'Low');
        setEditDetailsPropertyId(ai.propertyID ? String(ai.propertyID) : '');
        setIsEditingDetails(true);
    };

    /**
     * Cancels the inline details edit without persisting any changes.
     */
    const handleCancelEditDetails = () => {
        setIsEditingDetails(false);
    };

    /**
     * Persists updated title, description, priority, and property for the action item.
     * Records every change in the audit history on the backend.
     * Initial Comments are intentionally excluded — they are immutable once created.
     */
    const handleSaveDetails = async () => {
        setIsSavingDetails(true);
        try {
            // Build payload — only include propertyID when a valid selection was made.
            // Omitting the field (rather than sending undefined/null) ensures the backend
            // int? property is null, so HasValue is false and no property change is recorded.
            const parsedPropertyId = editDetailsPropertyId
                ? parseInt(editDetailsPropertyId, 10)
                : null;

            const payload = {
                title: editDetailsTitle,
                description: editDetailsDescription,
                priority: editDetailsPriority,
                userId: user.userID,
                // Only add the propertyID key when a valid integer was provided
                ...(parsedPropertyId !== null && !isNaN(parsedPropertyId)
                    ? { propertyID: parsedPropertyId }
                    : {})
            };

            const response = await apiFetch(`/api/ActionItems/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Action item details updated.');
                setIsEditingDetails(false);
                // Reload so all derived UI (title, sidebar, etc.) reflects the new values
                fetchActionItem();
            } else {
                toast.error(data.message || 'Failed to update details.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while saving details.');
        } finally {
            setIsSavingDetails(false);
        }
    };

    /**
     * Re-fetches the original email content from the mailbox via Graph API.
     * Updates the action item description with properly resolved inline images.
     * Dynamically searches both the monitored folder and the processed/archive folder
     * to locate the email regardless of where it was moved after processing.
     */
    const handleRefetchEmail = async () => {
        setIsRefetchingEmail(true);
        setRefetchResult(null);
        try {
            const response = await apiFetch(`/api/ActionItems/${id}/refetch-email`, {
                method: 'POST'
            });
            const data = await response.json();
            if (data.success) {
                setRefetchResult({
                    type: 'success',
                    message: data.message
                });
                toast.success(data.message || 'Email content re-fetched successfully.');
                // Reload the action item to reflect the updated description
                fetchActionItem();
            } else {
                setRefetchResult({
                    type: 'error',
                    message: data.message
                });
                toast.error(data.message || 'Failed to re-fetch email content.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setRefetchResult({
                type: 'error',
                message: 'An unexpected error occurred while re-fetching email content.'
            });
            toast.error('An error occurred while re-fetching email content.');
        } finally {
            setIsRefetchingEmail(false);
        }
    };

    /**
     * Saves an updated PropertyID for the action item (Admin only).
     * Records the change in the action item history for a human-readable audit trail.
     * @param {number} newPropertyId - The new PropertyID to assign
     */
    const handlePropertyUpdate = async (newPropertyId) => {
        if (!newPropertyId || newPropertyId === actionItem?.actionItem?.propertyID) {
            setShowPropertyEditor(false);
            return;
        }
        setIsSavingProperty(true);
        try {
            const response = await apiFetch(`/api/ActionItems/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    propertyID: parseInt(newPropertyId, 10),
                    userId: user.userID
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Property updated successfully.');
                setShowPropertyEditor(false);
                // Reload the action item to reflect the change
                fetchActionItem();
            } else {
                toast.error(data.message || 'Failed to update property.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while updating the property.');
        } finally {
            setIsSavingProperty(false);
        }
    };

    /**
     * Saves an updated completion progress percentage (0–100).
     * Calls the general PUT endpoint with only the progressPercent field.
     * @param {number} newProgress - The new progress value (0-100)
     */
    const handleSaveProgress = async (newProgress) => {
        const clampedValue = Math.max(0, Math.min(100, Number(newProgress)));
        setIsSavingProgress(true);
        try {
            const response = await apiFetch(`/api/ActionItems/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ progressPercent: clampedValue, userId: user.userID })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Progress updated.');
                setProgressValue(clampedValue);
                setIsEditingProgress(false);
                fetchActionItem();
            } else {
                toast.error(data.message || 'Failed to update progress.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while saving progress.');
        } finally {
            setIsSavingProgress(false);
        }
    };

    /**
     * Persist a new tag-set to the backend.
     * Constructs a trimmed, deduplicated comma-separated string from the given array.
     * @param {string[]} tagArray - The new array of tags to save
     */
    const saveTags = async (tagArray) => {
        // Deduplicate and sanitise tags before saving
        const cleanedTags = [...new Set(tagArray.map(t => t.trim()).filter(Boolean))];
        const tagString = cleanedTags.join(', ');
        setIsSavingTags(true);
        try {
            const response = await apiFetch(`/api/ActionItems/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tags: tagString, userId: user.userID })
            });
            const data = await response.json();
            if (data.success) {
                setTagsValue(tagString);
            } else {
                toast.error(data.message || 'Failed to update tags.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while saving tags.');
        } finally {
            setIsSavingTags(false);
        }
    };

    /**
     * Add a new tag to the tag list and persist.
     * Prevents empty or duplicate tags from being added.
     */
    const handleAddTag = () => {
        const trimmedTag = newTagInput.trim();
        if (!trimmedTag) return;

        // Parse the existing tags into an array for manipulation
        const currentTags = tagsValue
            ? tagsValue.split(',').map(t => t.trim()).filter(Boolean)
            : [];

        if (currentTags.map(t => t.toLowerCase()).includes(trimmedTag.toLowerCase())) {
            toast.info('That tag already exists.');
            setNewTagInput('');
            return;
        }

        const updatedTags = [...currentTags, trimmedTag];
        setNewTagInput('');
        saveTags(updatedTags);
    };

    /**
     * Remove a tag from the tag list and persist.
     * @param {string} tagToRemove - The tag string to remove
     */
    const handleRemoveTag = (tagToRemove) => {
        const currentTags = tagsValue
            ? tagsValue.split(',').map(t => t.trim()).filter(Boolean)
            : [];
        const updatedTags = currentTags.filter(t => t !== tagToRemove);
        saveTags(updatedTags);
    };

    /**
     * Save a due date change via the general PUT endpoint.
     * @param {string} newDateValue - ISO date string from a date input
     */
    const handleSaveDueDate = async (newDateValue) => {
        try {
            const response = await apiFetch(`/api/ActionItems/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dueDate: newDateValue ? new Date(newDateValue).toISOString() : null,
                    userId: user.userID
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Due date updated.');
                fetchActionItem();
            } else {
                toast.error(data.message || 'Failed to update due date.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while updating the due date.');
        }
    };

    /**
     * Delete (soft delete) a file attachment
     * @param {number} attachmentId - The attachment ID to delete
     */
    const deleteAttachment = async (attachmentId) => {
        if (!confirm('Are you sure you want to delete this attachment?')) return;
        
        try {
            const res = await apiFetch(`/api/ActionItems/attachments/${attachmentId}?userId=${currentUserId}`, {
                method: 'DELETE'
            });
            
            const data = await res.json();
            if (data.success) {
                toast.success('Attachment deleted');
                fetchAttachments();
            } else {
                toast.error(data.message || 'Failed to delete attachment');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error deleting attachment');
        }
    };

    /**
     * Handle file input change event
     * @param {Event} e - The file input change event
     */
    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file size (max 25MB)
            if (file.size > 25 * 1024 * 1024) {
                toast.error('File size must be less than 25MB');
                return;
            }
            uploadAttachment(file);
        }
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Format file size for display
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size string
     */
    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    /**
     * Performs an authenticated file download for an attachment.
     *
     * The download endpoint (/api/ActionItems/attachments/{id}/download) requires a JWT
     * Bearer token, so a plain browser navigation (<a href>) results in HTTP 401.
     * This function uses apiFetch (which automatically injects the Authorization header)
     * to retrieve the file as a Blob, then triggers a browser save-as dialogue via a
     * temporary object URL — all without exposing the token in the URL.
     *
     * @param {Object} attachment - The attachment object (must contain downloadUrl and fileName).
     */
    const handleDownloadAttachment = async (attachment) => {
        try {
            const response = await apiFetch(attachment.downloadUrl);
            if (!response.ok) {
                toast.error(`Download failed: ${response.status} ${response.statusText}`);
                return;
            }

            // Read the response body as a binary blob so we can save it to disk
            const blob = await response.blob();

            // Create a temporary anchor element pointing at an in-memory object URL and
            // programmatically click it to trigger the browser's save-as dialogue.
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = attachment.fileName || 'download';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);

            // Release the object URL after a configured delay to allow the download to start
            setTimeout(() => URL.revokeObjectURL(objectUrl), DOWNLOAD_URL_CLEANUP_DELAY_MS);
        } catch (err) {
            toast.error('Failed to download file. Please try again.');
            console.error('[handleDownloadAttachment]', err);
        }
    };

    /**
     * Escalate a comment to a new action item
     * @param {Object} comment - The comment log entry to escalate
     */
    const escalateCommentToActionItem = async (comment) => {
        if (!comment) return;
        
        try {
            const payload = {
                propertyID: actionItem.actionItem.propertyID,
                title: `Escalated from Action Item #${actionItem.actionItem.id}`,
                description: comment.comment,
                priority: 'Medium',
                comments: `Escalated from comment by ${getUserName(comment.changedByUserId)} on Action Item #${actionItem.actionItem.id}`,
                assignedToUserID: currentUserId,
                userID: currentUserId,
                parentActionItemId: actionItem.actionItem.id,
                escalatedFromCommentLogId: comment.logId,
                dataJson: null
            };
            
            const res = await apiFetch('/api/ActionItems/action-items/manual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            const data = await res.json();
            if (data.success) {
                const newActionItemId = data.data?.id || data.id;
                toast.success(`Comment escalated to Action Item #${newActionItemId}`);
                setShowEscalateCommentModal(false);
                setSelectedCommentForEscalation(null);
                // Refresh the current action item to show escalation status
                fetchActionItem();
                // Navigate to the new action item
                if (newActionItemId) {
                    navigate(`/action-items/${newActionItemId}`);
                }
            } else {
                toast.error(data.message || 'Failed to escalate comment');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error escalating comment:', e);
            toast.error('Error escalating comment');
        }
    };

    // ------------------------------------------------------------------------
    // API CALLS - Update Actions
 // ------------------------------------------------------------------------

    /**
* Assign an action item to a specific user
     * @param {number} assignedToUserId - The ID of the user to assign
*/
    const assignUser = async (assignedToUserId) => {
     if (!actionItem?.actionItem) return;
    
        try {
            const res = await apiFetch('/api/ActionItems/assign-user', {
     method: 'POST',
        headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify({
        actionItemId: actionItem.actionItem.id,
         assignedToUserId,
      changedByUserId: currentUserId
    })
      });
     
            const data = await res.json();
   if (data.success) {
 toast.success('Assignment updated');
       setShowUserSuggestions(false);
   setShowUserEditor(false);
        fetchActionItem();
       } else {
            toast.error('Assignment failed');
        }
        } catch (catchError) {
            Sentry.captureException(catchError);
     toast.error('Assignment failed');
      }
    };

    /**
     * Update the requester (person the ticket is raised on behalf of).
     * Diagnostics are re-fetched automatically when actionItem.requesterUserID changes
     * because the fetchDiagnostics effect depends on it.
     */
    const assignRequester = async (requesterUserId) => {
        if (!actionItem?.actionItem) return;
        setIsSavingRequester(true);
        try {
            const res = await apiFetch(`/api/ActionItems/${actionItem.actionItem.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ requesterUserID: requesterUserId, userId: currentUserId })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Requester updated');
                const matchedUser = allUsers.find(u => (u.userID ?? u.id) === requesterUserId) || null;
                setShowRequesterEditor(false);
                setShowRequesterSuggestions(false);

                // Auto-populate contact fields from the selected requester
                if (matchedUser) {
                    const displayName = getUserDisplay(matchedUser);
                    const phone = matchedUser.phoneNumber || matchedUser.phone || '';
                    const email = matchedUser.email || '';

                    setTenantContactName(displayName);
                    setTenantContactNumber(phone);
                    setTenantContactEmail(email);
                    saveContactField('tenantContactName', displayName);
                    if (phone) saveContactField('tenantContactNumber', phone);
                    if (email) saveContactField('tenantContactEmail', email);
                }

                fetchActionItem();
            } else {
                toast.error(data.message || 'Failed to update requester');
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error('Failed to update requester');
        } finally {
            setIsSavingRequester(false);
        }
    };

    /**
     * Assign an action item to a department (AND relationship with user and property)
     * @param {number} departmentId - The ID of the department to assign
     */
    const assignDepartment = async (departmentId) => {
        if (!actionItem?.actionItem) return;
        
        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/assign-department`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    departmentId,
                    changedByUserId: currentUserId
                })
            });
            
            const data = await res.json();
            if (data.success) {
                toast.success('Assigned to department');
                setShowDepartmentEditor(false);
                fetchActionItem();
            } else {
                toast.error(data.message || 'Department assignment failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Department assignment failed');
        }
    };

    /**
     * Assign an action item to a property (AND relationship with user and department).
     * Users marked as "Responsible" for this property will see the action item.
     * @param {number} propertyId - The ID of the property to assign (0 to clear)
     */
    const assignProperty = async (propertyId) => {
        if (!actionItem?.actionItem) return;

        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/assign-property`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    propertyId,
                    changedByUserId: currentUserId
                })
            });

            const data = await res.json();
            if (data.success) {
                toast.success('Assigned to property');
                setShowPropertyAssignEditor(false);
                fetchActionItem();
            } else {
                toast.error(data.message || 'Property assignment failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Property assignment failed');
        }
    };

    /**
     * Escalate the action item to the assigned user's manager
     * @param {string} reason - The reason for escalation
     */
    const escalateActionItem = async (reason) => {
        if (!actionItem?.actionItem) return;
        
        setIsEscalating(true);
        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/escalate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    escalatedByUserId: currentUserId,
                    reason: reason || 'No reason provided'
                })
            });
            
            const data = await res.json();
            if (data.success) {
                toast.success('Action item escalated to manager');
                setShowEscalateModal(false);
                setEscalationReason('');
                fetchActionItem();
            } else {
                toast.error(data.message || 'Escalation failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Escalation failed');
        } finally {
            setIsEscalating(false);
        }
    };

    /**
     * De-escalate the action item (remove escalation)
     */
    const deEscalateActionItem = async () => {
        if (!actionItem?.actionItem) return;
        
        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/de-escalate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUserId
                })
            });
            
            const data = await res.json();
            if (data.success) {
                toast.success('Escalation removed');
                fetchActionItem();
            } else {
                toast.error(data.message || 'De-escalation failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('De-escalation failed');
        }
    };

    /**
     * Save a tenant contact field (name, number, or email)
   * @param {string} field - The field name to update
   * @param {string} value - The new value
     */
    const saveContactField = async (field, value) => {
      if (!actionItem?.actionItem) return;
        
        try {
          const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/contact`, {
         method: 'PUT',
     headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({ field, value, userId: currentUserId })
          });
       
            const data = await res.json();
     if (data.message === 'No change detected.') return;
            if (data.success) {
        toast.success('Contact info updated');
            } else {
    toast.error('Save failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
          toast.error('Save failed');
 }
     };

    /**
     * Look up a user by name, email, or phone number and auto-populate the other contact fields.
     * Searches allUsers first; no external API call needed since allUsers includes all app users.
     * @param {'name'|'email'|'phone'} searchBy - Which field triggered the lookup
     * @param {string} value - The value to search for
     */
    const lookupAndPopulateContact = (searchBy, value) => {
        if (!value || !allUsers.length) return;
        const lower = value.toLowerCase().trim();
        let matched = null;

        if (searchBy === 'name') {
            matched = allUsers.find(u => getUserDisplay(u).toLowerCase() === lower);
        } else if (searchBy === 'email') {
            matched = allUsers.find(u => u.email && u.email.toLowerCase() === lower);
        } else if (searchBy === 'phone') {
            matched = allUsers.find(u =>
                (u.phoneNumber && u.phoneNumber === value) ||
                (u.phone && u.phone === value)
            );
        }

        if (!matched) return;

        // Auto-fill the other two fields
        if (searchBy !== 'name') {
            const name = getUserDisplay(matched);
            setTenantContactName(name);
            saveContactField('tenantContactName', name);
        }
        if (searchBy !== 'phone') {
            const phone = matched.phoneNumber || matched.phone || '';
            if (phone) {
                setTenantContactNumber(phone);
                saveContactField('tenantContactNumber', phone);
            }
        }
        if (searchBy !== 'email') {
            const email = matched.email || '';
            if (email) {
                setTenantContactEmail(email);
                saveContactField('tenantContactEmail', email);
            }
        }
    };

    // ------------------------------------------------------------------------
    // EVENT HANDLERS - User Assignment
    // ------------------------------------------------------------------------

    /**
     * Assign the current user to this action item
     */
    const handleAssignYourself = () => {
   if (currentUserId) assignUser(currentUserId);
    };

    // ------------------------------------------------------------------------
    // EVENT HANDLERS - Priority and Status Changes
    // ------------------------------------------------------------------------

/**
     * Handle priority dropdown change
  */
    const handlePriorityChange = async (e) => {
        if (!actionItem?.actionItem) return;

        const newPriority = e.target.value;
        setPriority(newPriority);
        setShowPrioritySelect(false);
        
        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/priority`, {
        method: 'PUT',
   headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({ priority: newPriority, userId: currentUserId })
       });
   
        const data = await res.json();
          if (data.success) {
             toast.success('Priority updated');
     } else {
     toast.error('Priority update failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Priority update failed');
 }
    };

    /**
     * Handle status dropdown change
     */
    const handleStatusChange = async (e) => {
        if (!actionItem?.actionItem) return;
        
        const newStatus = e.target.value;
   setStatus(newStatus);
    setShowStatusSelect(false);
        
     try {
const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/status`, {
       method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: newStatus, userID: currentUserId })
            });
     
            const data = await res.json();
   if (data.success) {
      toast.success('Status updated');
       } else {
     toast.error('Status update failed');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
          toast.error('Status update failed');
        }
  };

    // ------------------------------------------------------------------------
    // EVENT HANDLERS - Comments
    // ------------------------------------------------------------------------

    /**
* Submit a new comment to the action item
     */
    const handleCommentSubmit = async () => {
        if (!actionItem?.actionItem || !commentText.trim()) return;
        
        setIsSubmittingComment(true);
 try {
     const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/comment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
     body: JSON.stringify({ userId: currentUserId, comment: commentText.trim() })
});
 
            const data = await res.json();
  if (data.success) {
    setCommentText('');
          toast.success('Comment added');
    fetchActionItem();
            } else {
     toast.error('Failed to add comment');
            }
    } catch (catchError) {
        Sentry.captureException(catchError);
            toast.error('Failed to add comment');
        }
        setIsSubmittingComment(false);
    };

    /**
     * Submit the current comment text as an internal/private note.
     * Internal notes are only visible to users with InternalNotePermission
     * on the action item's board (configured per-department in board settings).
     */
    const handleInternalNoteSubmit = async () => {
        if (!actionItem?.actionItem || !commentText.trim()) return;

        setIsSubmittingComment(true);
        try {
            const res = await apiFetch(`/api/ActionItems/action-items/${actionItem.actionItem.id}/comment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: currentUserId,
                    comment: commentText.trim(),
                    isInternalNote: true
                })
            });

            const data = await res.json();
            if (data.success) {
                setCommentText('');
                toast.success('Internal note added');
                fetchActionItem();
            } else {
                toast.error(data.message || 'Failed to add internal note');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to add internal note');
        }
        setIsSubmittingComment(false);
    };

    // ------------------------------------------------------------------------
    // UTILITY FUNCTIONS - User Name Lookup
    // ------------------------------------------------------------------------

    /**
     * Get user name by ID from all available user lists
     * @param {number} userId - The user ID to look up
     * @returns {string} User display name or fallback
     */
    const getUserName = (userId, commentText = null) => {
        if (!userId || userId === 0) {
            // For system-authored entries that are inbound email replies,
            // extract the sender name from the stored comment text
            // Format: "Email reply from {name} <{email}>:\n\n{body}"
            if (commentText) {
                const emailReplyMatch = commentText.match(/^Email reply from (.+?) <[^>]+>:/);
                if (emailReplyMatch) return emailReplyMatch[1];
            }
            return 'System';
        }
        const foundUser = allUsers.find(x => (x.userID ?? x.id) === userId)
            || centreManagers.find(x => x.userID === userId);

        if (!foundUser) return `User ${userId}`;
        return getUserDisplay(foundUser);
    };

    // ------------------------------------------------------------------------
    // UTILITY FUNCTIONS - Validation
    // ------------------------------------------------------------------------

    /**
     * Validate that a name contains only letters, spaces, hyphens, apostrophes, and accented characters
     */
    const isValidName = (name) => /^[\p{L}\s\-']*$/u.test(name);

    /**
     * Validate that a number contains only digits
     */
    const isValidNumber = (number) => /^\d*$/.test(number);

    /**
     * Validate email format (empty string is allowed)
     */
    const isValidEmail = (email) => email === '' || /^[^@]+@[^@]+\.[^@]+$/.test(email);

    /**
     * Format a date/time in the format "28 November 2025 08:32" (24-hour clock)
     * @param {string|Date} dateValue - The date value to format
     * @returns {string} Formatted date-time string
     */
    const formatCommentDateTime = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
            const date = new Date(dateValue);
            const day = date.getDate();
            const month = date.toLocaleDateString('en-GB', { month: 'long' });
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `(${day} ${month} ${year} ${hours}:${minutes})`;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return 'N/A';
        }
    };

    // ------------------------------------------------------------------------
    // UTILITY FUNCTIONS - Activity Log Rendering
    // ------------------------------------------------------------------------

    /**
     * Get the appropriate icon for a log action type
     * @param {string} actionType - The type of action logged
     * @returns {JSX.Element} Icon component
     */
    const getLogIcon = (actionType) => {
        switch (actionType) {
            case 'Created':
                return <HiOutlineDocumentAdd size={20} />;
            case 'StatusChanged':
                return <FaRegDotCircle size={20} />;
            case 'Assignment':
                return <MdPersonAddAlt1 size={20} />;
            case 'PriorityChanged':
                return <FaExclamationCircle size={20} />;
            case 'ContactFieldChanged':
                return <FaAddressBook size={20} />;
            case 'AttachmentAdded':
                return <FaPaperclip size={20} />;
            case 'AttachmentDeleted':
                return <FaTrashAlt size={20} />;
            case 'CollaboratorAdded':
                return <FaUsers size={20} />;
            case 'CollaboratorRemoved':
                return <FaUserMinus size={20} />;
            case 'Escalated':
                return <FaLevelUpAlt size={20} />;
            case 'DeEscalated':
                return <FaArrowDown size={20} />;
            case 'AssignedToDepartment':
                return <FaBuilding size={20} />;
            case 'Updated':
                return <FaEdit size={20} />;
            case 'Merged':
            case 'MergedComment':
                return <FaCodeBranch size={20} />;
            case 'CommentEscalated':
                return <FaLevelUpAlt size={20} />;
            case 'RouterAlertUpdate':
                return <FaCheckCircle size={20} />;
            default:
                return <HiOutlineDocumentAdd size={20} />;
        }
    };

    /**
     * Format log entry text based on action type.
     * Every known action type renders a descriptive, human-readable sentence so that
     * the timeline is always meaningful and never falls back to "did something".
     * @param {Object} log - Log entry object
     * @returns {JSX.Element} Formatted log text
     */
    const formatLogText = (log) => {
        const userName = <span className='log-username'>{getUserName(log.changedByUserId)}</span>;
        const date = (
            <span className='log-date'>
                {new Date(log.changedAt).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                })}
            </span>
        );

        switch (log.actionType) {
            // ---- Creation -------------------------------------------------------
            case 'Created':
                return <>{userName} created this action item on {date}</>;

            // ---- Status ---------------------------------------------------------
            case 'StatusChanged':
                return (
                    <>{userName} changed status from <strong>{log.oldValue}</strong> to <strong>{log.newValue}</strong> on {date}</>
                );

            // ---- Assignment -----------------------------------------------------
            case 'Assignment': {
                const assignedUserId = Number(log.newValue);
                if (assignedUserId === log.changedByUserId) {
                    return <>{userName} self-assigned on {date}</>;
                }
                const assignedUserName = <span className='log-username'>{getUserName(assignedUserId)}</span>;
                return <>{userName} assigned to {assignedUserName} on {date}</>;
            }

            // ---- Priority -------------------------------------------------------
            case 'PriorityChanged':
                return (
                    <>{userName} changed priority from <strong>{log.oldValue}</strong> to <strong>{log.newValue}</strong> on {date}</>
                );

            // ---- Contact fields -------------------------------------------------
            case 'ContactFieldChanged': {
                const fieldName = log.comment?.startsWith('Field: ')
                    ? log.comment.replace('Field: ', '')
                    : 'a contact field';
                return (
                    <>{userName} updated <em>{fieldName}</em> from <strong>{log.oldValue || '(empty)'}</strong> to <strong>{log.newValue || '(empty)'}</strong> on {date}</>
                );
            }

            // ---- Attachments ----------------------------------------------------
            case 'AttachmentAdded':
                return (
                    <>{userName} attached <strong>{log.newValue || 'a file'}</strong> on {date}</>
                );

            case 'AttachmentDeleted':
                return (
                    <>{userName} removed attachment <strong>{log.oldValue || 'a file'}</strong> on {date}</>
                );

            // ---- Collaborators --------------------------------------------------
            case 'CollaboratorAdded': {
                const collabName = log.newValue
                    ? <span className='log-username'>{log.newValue}</span>
                    : 'a collaborator';
                return <>{userName} added {collabName} as a collaborator on {date}</>;
            }

            case 'CollaboratorRemoved': {
                const removedName = log.oldValue
                    ? <span className='log-username'>{log.oldValue}</span>
                    : 'a collaborator';
                return <>{userName} removed {removedName} from collaborators on {date}</>;
            }

            // ---- Department assignment ------------------------------------------
            case 'AssignedToDepartment':
                return (
                    <>{userName} assigned to department <strong>{log.newValue || 'Unknown'}</strong> on {date}</>
                );

            // ---- Escalation -----------------------------------------------------
            case 'Escalated':
                return (
                    <>{userName} escalated this item to <strong>{log.newValue || 'a manager'}</strong> on {date}</>
                );

            case 'DeEscalated':
                return <>{userName} resolved the escalation on {date}</>;

            case 'CommentEscalated':
                return (
                    <>{userName} escalated a comment to a new Action Item on {date}</>
                );

            // ---- General update -------------------------------------------------
            case 'Updated':
                // The backend stores a semicolon-separated list of changes in log.comment
                if (log.comment) {
                    return (
                        <>
                            {userName} updated this item on {date}: <em>{log.comment}</em>
                        </>
                    );
                }
                return <>{userName} updated this item on {date}</>;

            // ---- Merge ----------------------------------------------------------
            case 'Merged': {
                // NewValue is either "Merged from #X" (target side) or "Merged into #X" (source side)
                const isMergedFrom = log.newValue?.startsWith('Merged from #');
                const isMergedInto = log.newValue?.startsWith('Merged into #');
                const linkedItemId = isMergedFrom
                    ? log.newValue.replace('Merged from #', '')
                    : isMergedInto
                        ? log.newValue.replace('Merged into #', '')
                        : log.oldValue;

                if (isMergedFrom) {
                    return (
                        <>
                            {userName} merged Action Item{' '}
                            <strong>#{linkedItemId}</strong> into this item on {date}
                        </>
                    );
                }
                if (isMergedInto) {
                    return (
                        <>
                            {userName} merged this item into Action Item{' '}
                            <strong>#{linkedItemId}</strong> on {date} — this item is now <strong>closed</strong>
                        </>
                    );
                }
                return <>{userName} merged action items on {date}</>;
            }

            // ---- Comments carried over from a merged item -----------------------
            // MergedComment entries are rendered separately in renderLogRow; this
            // fallback label is shown if they somehow reach this path.
            case 'MergedComment':
                return (
                    <>
                        Comment transferred from merged Action Item{' '}
                        <strong>#{log.oldValue}</strong> on {date}
                    </>
                );

            // ---- Fallback -------------------------------------------------------
            default:
                // Log the unhandled type in development to help catch future gaps.
                if (process.env.NODE_ENV === 'development') {
                    console.warn(`[ActionItemDetailPage] Unhandled log actionType: "${log.actionType}"`, log);
                }
                return (
                    <>{userName} performed action <em>{log.actionType}</em> on {date}</>
                );
        }
    };

    /**
     * Resolve a markdown link/image target (and optional label) to a known attachment
     * on this action item, so comment links can be opened locally via the authenticated,
     * Graph-proxied endpoint and PDFs can be annotated.
     *
     * Resolution strategy (most reliable first):
     *  1. App download URL carrying the attachment id: /api/ActionItems/attachments/{id}/download
     *  2. Filename match against the attachments list (upgrades legacy raw-SharePoint links,
     *     which used the original file name as the link label).
     *
     * @param {string} href  - The link target from the markdown source.
     * @param {string} label - The visible link text (usually the filename).
     * @returns {Object|null} { attachmentId, downloadUrl, fileName, contentType } or null.
     */
    const resolveCommentAttachment = useCallback((href, label) => {
        if (!Array.isArray(attachments) || attachments.length === 0) return null;

        // 1. Pull an attachment id straight out of the app download URL.
        if (typeof href === 'string') {
            const match = href.match(/\/api\/ActionItems\/attachments\/(\d+)\/download/i);
            if (match) {
                const id = parseInt(match[1], 10);
                const byId = attachments.find(a => a.attachmentId === id);
                if (byId) {
                    return {
                        attachmentId: byId.attachmentId,
                        downloadUrl: byId.downloadUrl || `/api/ActionItems/attachments/${byId.attachmentId}/download`,
                        fileName: byId.fileName,
                        contentType: byId.contentType
                    };
                }
                // Id not in the loaded list (e.g. stale) — still return a usable object.
                return {
                    attachmentId: id,
                    downloadUrl: `/api/ActionItems/attachments/${id}/download`,
                    fileName: label || 'attachment',
                    contentType: ''
                };
            }
        }

        // 2. Fall back to matching by filename (upgrades legacy SharePoint links).
        const name = (label || '').trim().toLowerCase();
        if (name) {
            const byName = attachments.find(a => a.fileName?.trim().toLowerCase() === name);
            if (byName) {
                return {
                    attachmentId: byName.attachmentId,
                    downloadUrl: byName.downloadUrl || `/api/ActionItems/attachments/${byName.attachmentId}/download`,
                    fileName: byName.fileName,
                    contentType: byName.contentType
                };
            }
        }

        return null;
    }, [attachments]);

    /**
     * Open the PDF annotation modal for an attachment resolved from a comment link.
     */
    const handleAnnotateFromLink = useCallback((attachment) => {
        if (!attachment) return;
        setSelectedPdfAttachment(attachment);
        setShowPdfAnnotationModal(true);
    }, [setSelectedPdfAttachment, setShowPdfAnnotationModal]);

    /**
     * Custom ReactMarkdown component overrides for comment bodies.
     * - Links to action-item attachments are intercepted so they open locally through the
     *   authenticated app endpoint (no SharePoint permissions needed) and PDFs gain an
     *   inline "Annotate" affordance.
     * - Inline images sourced from the authenticated app endpoint are fetched with the JWT.
     * Memoised so the override object identity is stable across renders.
     */
    const commentMarkdownComponents = useMemo(() => ({
        a: ({ href, children }) => (
            <CommentAttachmentLink
                href={href}
                resolveAttachment={resolveCommentAttachment}
                onAnnotate={handleAnnotateFromLink}
            >
                {children}
            </CommentAttachmentLink>
        ),
        img: ({ src, alt }) => <CommentAttachmentImage src={src} alt={alt} />
    }), [resolveCommentAttachment, handleAnnotateFromLink]);

    /**
     * Render a comment body. HTML email bodies are shown in a sandboxed iframe;
     * plain text / Markdown is rendered with ReactMarkdown using the attachment-aware
     * link/image overrides above.
     * @param {string} commentText - The raw comment content.
     * @returns {JSX.Element} Rendered comment body.
     */
    const renderCommentBody = (commentText) => {
        if (containsHtml(commentText)) {
            return <SandboxedEmailContent html={commentText} />;
        }
        return (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={commentMarkdownComponents}>
                {commentText || ''}
            </ReactMarkdown>
        );
    };

    /**
     * Render a single log row.
     * Handles three distinct visual styles:
     *  1. Comment            — full comment bubble with escalate option
     *  2. MergedComment      — comment bubble with a "from merged item" badge (read-only)
     *  3. All other types    — compact action row with icon + descriptive text
     * @param {Object} log - Log entry to render
     * @returns {JSX.Element} Rendered log row
     */
    const renderLogRow = (log) => {
        // ---- 1a. Internal notes (private comments visible only to permitted staff) ---
        if (log.actionType === 'InternalNote') {
            return (
                <div
                    className='action-item-log-comment-row internal-note'
                    key={log.logId}
                >
                    <div className='comment-header'>
                        <span className='comment-username'>{getUserName(log.changedByUserId, log.comment)}</span>
                        <span className='comment-date'>{formatCommentDateTime(log.changedAt)}</span>
                        <span className='internal-note-badge' title='Internal note — only visible to permitted staff'>
                            <FaLock size={11} /> Internal Note
                        </span>
                    </div>
                    <div className='comment-body comment-body-markdown internal-note-body'>
                        {renderCommentBody(log.comment)}
                    </div>
                </div>
            );
        }

        // ---- 1. Regular comments (escapable) ------------------------------------
        if (log.actionType === 'Comment') {
            return (
                <div
                    className={`action-item-log-comment-row ${log.isEscalated ? 'escalated' : ''}`}
                    key={log.logId}
                >
                    <div className='comment-header'>
                        <span className='comment-username'>{getUserName(log.changedByUserId, log.comment)}</span>
                        <span className='comment-date'>{formatCommentDateTime(log.changedAt)}</span>
                        {log.isEscalated ? (
                            <span
                                className='escalated-badge'
                                title={`Escalated to Action Item #${log.escalatedToActionItemId}`}
                            >
                                <FaLevelUpAlt size={12} /> Escalated to #{log.escalatedToActionItemId}
                            </span>
                        ) : (
                            <button
                                className='escalate-comment-btn'
                                onClick={() => {
                                    setSelectedCommentForEscalation(log);
                                    setShowEscalateCommentModal(true);
                                }}
                                title='Escalate this comment to a new Action Item'
                            >
                                <FaLevelUpAlt size={12} /> Escalate
                            </button>
                        )}
                    </div>
                    <div className='comment-body comment-body-markdown'>
                        {renderCommentBody(log.comment)}
                    </div>
                </div>
            );
        }

        // ---- 2. Comments transferred from a merged action item ------------------
        // These are normally shown in the dedicated MergedCommentsSection component
        // (grouped by source item, below the native comment stream).  This branch
        // handles any edge cases where a MergedComment still reaches renderLogRow —
        // e.g. the History tab or a fallback render path.  The badge is now a
        // clickable link so users can navigate to the source item.
        if (log.actionType === 'MergedComment') {
            const sourceItemId = log.oldValue;
            const originalAuthorId = Number(log.newValue);

            return (
                <div className='action-item-log-comment-row merged-comment' key={log.logId}>
                    <div className='comment-header'>
                        <span className='comment-username'>{getUserName(originalAuthorId)}</span>
                        <span className='comment-date'>{formatCommentDateTime(log.changedAt)}</span>
                        {/* Clickable badge — navigates to the (now-closed) source item */}
                        <button
                            type='button'
                            className='merged-comment-badge'
                            style={{ cursor: 'pointer' }}
                            title={`View source Action Item #${sourceItemId}`}
                            onClick={() => navigate(`/action-items/${sourceItemId}`)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate(`/action-items/${sourceItemId}`);
                                }
                            }}
                        >
                            <FaCodeBranch size={12} /> From merged item #{sourceItemId}
                        </button>
                    </div>
                    <div className='comment-body comment-body-markdown'>
                        {renderCommentBody(log.comment)}
                    </div>
                </div>
            );
        }

        // ---- 3a. System-generated outage event (new outage / additional alert) ---
        // RouterAlertOutage entries are written when a new outage is detected or an
        // additional alert type fires during an ongoing outage. Shown with a
        // red/warning badge so engineers immediately see it is a problem event.
        if (log.actionType === 'RouterAlertOutage') {
            const isNewOutage = log.comment?.includes('New') && log.comment?.includes('outage detected');
            return (
                <div className='action-item-log-comment-row router-alert-update router-alert-outage' key={log.logId}>
                    <div className='comment-header'>
                        <span className='comment-username system-username'>
                            <FaExclamationCircle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            Router Monitoring
                        </span>
                        <span className='comment-date'>{formatCommentDateTime(log.changedAt)}</span>
                        <span className={`merged-comment-badge ${isNewOutage ? 'router-outage-badge' : 'router-additional-badge'}`}>
                            <FaExclamationCircle size={12} /> {isNewOutage ? 'Outage Detected' : 'Additional Alert'}
                        </span>
                    </div>
                    <div className='comment-body'>
                        {log.comment || ''}
                    </div>
                </div>
            );
        }

        // ---- 3b. System-generated recovery event (router/metric recovered) --------
        // RouterAlertResolved entries are written when a router comes back online
        // or a threshold metric returns to normal. Shown with a green badge.
        if (log.actionType === 'RouterAlertResolved') {
            // Determine the headline from the text content
            const isBackOnline = log.comment?.includes('Router Back Online');
            const badgeLabel = isBackOnline ? 'Router Back Online' : 'Threshold Normalized';
            return (
                <div className='action-item-log-comment-row router-alert-update router-alert-resolved' key={log.logId}>
                    <div className='comment-header'>
                        <span className='comment-username system-username'>
                            <FaCheckCircle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            Router Monitoring
                        </span>
                        <span className='comment-date'>{formatCommentDateTime(log.changedAt)}</span>
                        <span className='merged-comment-badge router-online-badge'>
                            <FaCheckCircle size={12} /> {badgeLabel}
                        </span>
                    </div>
                    <div className='comment-body'>
                        {log.comment || ''}
                    </div>
                </div>
            );
        }

        // ---- 3c. Legacy RouterAlertUpdate (backward compat for existing entries) --
        // Older entries used the generic RouterAlertUpdate type for both outage and
        // recovery events. Render with a neutral system badge.
        if (log.actionType === 'RouterAlertUpdate') {
            return (
                <div className='action-item-log-comment-row router-alert-update' key={log.logId}>
                    <div className='comment-header'>
                        <span className='comment-username system-username'>
                            <FaRegDotCircle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            Router Monitoring
                        </span>
                        <span className='comment-date'>{formatCommentDateTime(log.changedAt)}</span>
                    </div>
                    <div className='comment-body comment-body-markdown'>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {log.comment || ''}
                        </ReactMarkdown>
                    </div>
                </div>
            );
        }

        // ---- 4. All other action log entries ------------------------------------
        return (
            <div className='action-item-log-row' key={log.logId}>
                <span className='action-item-log-icon'>{getLogIcon(log.actionType)}</span>
                <span className='action-item-log-details'>{formatLogText(log)}</span>
            </div>
        );
    };

    // ------------------------------------------------------------------------
  // RENDER - Loading State
    // ------------------------------------------------------------------------

    if (!actionItem) {
        return <div>Loading...</div>;
    }

    // ------------------------------------------------------------------------
    // RENDER - Main Component
    // ------------------------------------------------------------------------

    // Determine if this is a non-compliance action item
    const isNonCompliance = actionItem?.actionItem?.actionItemsType === 'Non-Compliance' || 
                           actionItem?.actionItem?.templateKey === 'NonCompliance' ||
                           actionItem?.actionItem?.relatedType === 'NonCompliance';

    const isDailyChecklist = actionItem?.actionItem?.actionItemsType === 'DailyChecklist';

    /**
     * Parse daily checklist data from dataJson
     * @returns {Object | null} Parsed checklist data with formData and templateKey
     */
    const getDailyChecklistData =() => {
        if (!isDailyChecklist || !actionItem?.actionItem?.dataJson) return null;
    
        try {
            const data = typeof actionItem.actionItem.dataJson === 'string' 
                ? JSON.parse(actionItem.actionItem.dataJson) 
                : actionItem.actionItem.dataJson;
        
            return {
                submissionDate: data.submissionDate,
                checklistCompleted: data.checklistCompleted,
                templateKey: data.templateKey,
                formData: data.formData || {},
                incompleteReason: data.incompleteReason
            };
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error parsing daily checklist data:', error);
            return null;
        }
    };

    /**
        * Render daily checklist from saved formData (no template needed)
        * Displays the checklist exactly as it was submitted, preserving historical accuracy
        */
    const renderDailyChecklistFromData = () => {
        const checklistData = getDailyChecklistData();
        if (!checklistData || !checklistData.formData) return null;

        // Group responses by section
        const sections = {};
        checklistData.formData.forEach(item => {
            const sectionTitle = item.section || 'General';
            if (!sections[sectionTitle]) {
                sections[sectionTitle] = {
                    title: sectionTitle,
                    description: item.sectionDescription || '',
                    questions: []
                };
            }
            sections[sectionTitle].questions.push(item);
        });

        return (
            <div className='submitted-form-section daily-checklist-view'>
                <h2>
                    <i className='fas fa-clipboard-check'></i>
                    Daily Checklist Submission
                </h2>
            
                <div className='checklist-metadata'>
                    <div className='metadata-row'>
                        <span className='metadata-label'>Submission Date:</span>
                        <span className='metadata-value'>
                            {new Date(checklistData.submissionDate).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </span>
                    </div>
                    <div className='metadata-row'>
                        <span className='metadata-label'>Status:</span>
                        <span className={`metadata-value checklist-status ${checklistData.checklistCompleted ? 'completed' : 'incomplete'}`}>
                            {checklistData.checklistCompleted ? '✓ Completed' : '✗ Incomplete'}
                        </span>
                    </div>
                    {checklistData.incompleteReason && (
                        <div className='metadata-row'>
                            <span className='metadata-label'>Reason:</span>
                            <span className='metadata-value incomplete-reason'>
                                {checklistData.incompleteReason}
                            </span>
                        </div>
                    )}
                </div>

                <div className='checklist-sections'>
                    {Object.values(sections).map((section, idx) => (
                        <div key={idx} className='checklist-section'>
                            <h3 className='section-title'>{section.title}</h3>
                            {section.description && (
                                <p className='section-description'>{section.description}</p>
                            )}
                            <div className='section-questions'>
                                {section.questions.map((question, qIdx) => (
                                    <div key={qIdx} className='checklist-question-row'>
                                        <div className='question-label'>
                                            {question.questionText}
                                            {question.required && <span className='required-indicator'>*</span>}
                                        </div>
                                        <div className='question-answer'>
                                            {renderAnswerValue(question)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    /**
     * Render the answer value based on field type.
     * Handles all template field types including signatures stored as base64 data URLs.
     */
    const renderAnswerValue = (question) => {
        const { fieldType, answer } = question;
    
        switch (fieldType) {
            case 'checkbox':
                return (
                    <span className={`checkbox-answer ${answer ? 'checked' : 'unchecked'}`}>
                        {answer ? '✓ Yes' : '✗ No'}
                    </span>
                );
            case 'textarea':
            case 'text':
                return <span className='text-answer'>{answer || 'N/A'}</span>;
            case 'select':
            case 'radio':
                return <span className='select-answer'>{answer || 'N/A'}</span>;
            case 'date':
                return <span className='date-answer'>{answer ? new Date(answer).toLocaleDateString('en-GB') : 'N/A'}</span>;
            case 'signature':
                // Render base64 data URL signatures as an actual image element
                if (answer && typeof answer === 'string' && answer.startsWith('data:image/')) {
                    return (
                        <img
                            src={answer}
                            alt="Signature"
                            className="signature-answer-image"
                        />
                    );
                }
                return <span className='default-answer'>No signature provided</span>;
            default:
                return <span className='default-answer'>{answer?.toString() || 'N/A'}</span>;
        }
    };

    /**
     * Get the template for the current action item based on templateKey or actionItemsType.
     * @returns {Object|null} The matching template or null if not found
     */
    const getCurrentTemplate = () => {
        const ai = actionItem?.actionItem;
        if (!ai || templates.length === 0) return null;
        
        // First try to match by templateKey
        if (ai.templateKey) {
            const template = templates.find(t => t.templateKey === ai.templateKey);
            if (template) return template;
        }
        
        // Fall back to matching by actionItemsType
        if (ai.actionItemsType) {
            // Map common action item types to template keys
            const typeToTemplateKey = {
                'Non-Compliance': 'NonCompliance',
                'Manual': 'Manual',
                'Ad-hoc': 'Manual',
                'SIPA': 'SIPA'
            };
            const mappedKey = typeToTemplateKey[ai.actionItemsType];
            if (mappedKey) {
                return templates.find(t => t.templateKey === mappedKey) || null;
            }
        }
        
        return null;
    };

    /**
     * Parse template schema to get field definitions.
     * Flattens row/column container fields so nested values (e.g. offboarding
     * employee/date fields) resolve correctly from dataJson.
     * @param {Object} template - The template object
     * @returns {Array} Array of data fields from the schema
     */
    const getTemplateFields = (template) => {
        if (!template?.schema) return [];
        try {
            const schema = typeof template.schema === 'string'
                ? JSON.parse(template.schema)
                : template.schema;

            const flattenFields = (fields = []) => fields.flatMap(field => {
                if (field?.type === 'row' && Array.isArray(field.columns)) {
                    return field.columns.flatMap(column => column?.fields || []);
                }
                return field;
            });

            // Support both flat fields and section-based schemas.
            const topLevelFields = flattenFields(schema.fields || []);
            const sectionFields = (schema.sections || []).flatMap(s => flattenFields(s.fields || []));
            return topLevelFields.length > 0 ? topLevelFields : sectionFields;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return [];
        }
    };

    /**
     * Get the value for a field from the action item data.
     * Checks actionItem properties first, then falls back to dataJson for custom fields.
     * @param {string} fieldName - The name of the field
     * @returns {string} The field value or 'N/A' if not found
     */
    const getFieldValue = (fieldName) => {
        const ai = actionItem?.actionItem;
        if (!ai) return 'N/A';
        
        // Check direct action item properties first
        if (ai[fieldName] !== undefined && ai[fieldName] !== null && ai[fieldName] !== '') {
            return ai[fieldName];
        }
        
        // Check dataJson for custom fields
        if (ai.dataJson) {
            try {
                const data = typeof ai.dataJson === 'string' ? JSON.parse(ai.dataJson) : ai.dataJson;
                if (data[fieldName] !== undefined && data[fieldName] !== null && data[fieldName] !== '') {
                    return data[fieldName];
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Ignore parse errors
            }
        }
        
        return 'N/A';
    };

    /**
     * Render form data based on template schema fields.
     * Falls back to hardcoded Non-Compliance fields if template has no schema.
     * @returns {JSX.Element|null} Rendered form fields
     */
    const renderTemplateFormData =() => {
        if (isDailyChecklist) {
            const ai = actionItem?.actionItem;
            return (
                <div className='submitted-form-section'>
                    <h2>
                        <i className='fas fa-clipboard-check'></i>
                        Daily Checklist
                    </h2>
                    <div className='form-data-display'>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Property:</span>
                            <span className='form-data-value'>{propertyName || 'N/A'}</span>
                        </div>
                        {ai?.title && (
                            <div className='form-data-row'>
                                <span className='form-data-label'>Title:</span>
                                <span className='form-data-value'>{ai.title}</span>
                            </div>
                        )}
                        {ai?.description && (
                            <div className='form-data-row form-data-row--column'>
                                <span className='form-data-label'>Description:</span>
                                <span className='form-data-value'>{ai.description}</span>
                            </div>
                        )}
                        {ai?.comments && (
                            <div className='form-data-row'>
                                <span className='form-data-label'>Initial Comments:</span>
                                <span className='form-data-value'>{ai.comments}</span>
                            </div>
                        )}
                        <div className='form-data-row'>
                            <span className='form-data-label'>Status:</span>
                            <span className='form-data-value'>{status || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Priority:</span>
                            <span className={`form-data-value priority-${(ai?.priority || 'low').toLowerCase()}`}>
                                {ai?.priority || 'Low'}
                            </span>
                        </div>
                    </div>
                </div>
            );
    }
        
    


        const currentTemplate = getCurrentTemplate();
        const templateFields = currentTemplate ? getTemplateFields(currentTemplate) : [];

        // If we have a template with defined fields, render them dynamically
        if (templateFields.length > 0) {
            // Check if the schema uses sections for grouped rendering
            let parsedSchema = null;
            try {
                parsedSchema = currentTemplate?.schema
                    ? (typeof currentTemplate.schema === 'string' ? JSON.parse(currentTemplate.schema) : currentTemplate.schema)
                    : null;
            } catch { /* ignore */ }
            const sections = parsedSchema?.sections || [];

            const formatFieldValue = (field, val) => {
                if (val === 'N/A' || val === null || val === undefined) return 'N/A';
                // Date fields — format as dd MMMM yyyy
                if (field.type === 'date' && val && val !== 'N/A') {
                    try {
                        const d = new Date(val);
                        if (!isNaN(d.getTime())) {
                            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
                        }
                    } catch { /* fall through */ }
                }
                // Entra user selector — value may be an object with displayName
                if (field.type === 'entra-user-selector' && val && typeof val === 'object') {
                    return val.displayName || val.name || val.email || JSON.stringify(val);
                }
                return val;
            };

            const formatInlineReadOnlyFieldValue = (value) => {
                return typeof value === 'object' ? (value?.displayName || value?.name || 'N/A') : value;
            };

            const editableInlineFieldTypes = new Set([
                'text',
                'email',
                'number',
                'phone',
                'url',
                'date',
                'time',
                'datetime',
                'textarea',
                'select',
                'entra-user-selector',
                'property-selector',
                'tenant-selector',
                'shop-number',
                'role-selector',
                'lookup-selector'
            ]);

            const renderFieldRow = (field) => {
                // Skip non-data fields (info alerts, decorative elements)
                if (!field?.name || field.type === 'info-alert') return null;
                const rawVal = getFieldValue(field.name);
                const fieldVal = formatFieldValue(field, rawVal);
                const isSignatureImage = field.type === 'signature' && typeof fieldVal === 'string' && fieldVal.startsWith('data:image');
                // Inline editing intentionally allows only types listed in editableInlineFieldTypes so we can
                // round-trip safely without coercing arrays/objects/booleans into strings or exposing read-only values.
                const isInlineFieldReadOnly = Boolean(field.readOnly) || field.type === 'computed-value';
                const canInlineEditField = !isInlineFieldReadOnly && editableInlineFieldTypes.has(field.type);

                // ── Checklist Group ─────────────────────────────────────────
                if (field.type === 'checklist-group') {
                    const savedValue = typeof fieldVal === 'object' && fieldVal !== null ? fieldVal : {};
                    const subItems = field.subItems || [];
                    const groupValue = isEditingTemplateData
                        ? (typeof editingDataJson[field.name] === 'object' && editingDataJson[field.name] !== null
                            ? editingDataJson[field.name]
                            : savedValue)
                        : savedValue;

                    return (
                        <div className='form-data-row form-data-row--column' key={field.name}>
                            <span className='form-data-label'>{field.label || field.name}:</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                                {subItems.length > 0 ? subItems.map((sub, idx) => {
                                    const subKey = sub.name || `sub_${idx}`;
                                    const subVal = groupValue[subKey] || { checked: false, comments: '' };
                                    const isChecked = subVal.checked === true || subVal.checked === 'true';

                                    if (isEditingTemplateData) {
                                        return (
                                            <div key={subKey} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                                    <input
                                                        type='checkbox'
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            const updated = { ...groupValue, [subKey]: { ...subVal, checked: e.target.checked } };
                                                            setEditingDataJson(prev => ({ ...prev, [field.name]: updated }));
                                                        }}
                                                    />
                                                    <span>{sub.label || subKey}</span>
                                                </label>
                                                <input
                                                    type='text'
                                                    className='details-edit-input'
                                                    style={{ flex: 1, minWidth: '120px', fontSize: '0.85rem' }}
                                                    placeholder='Comments…'
                                                    value={subVal.comments || ''}
                                                    onChange={(e) => {
                                                        const updated = { ...groupValue, [subKey]: { ...subVal, comments: e.target.value } };
                                                        setEditingDataJson(prev => ({ ...prev, [field.name]: updated }));
                                                    }}
                                                />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={subKey} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                                            <span style={{ fontWeight: 700, width: '18px', textAlign: 'center', color: isChecked ? '#388e3c' : '#d32f2f' }}>
                                                {isChecked ? '✓' : '✗'}
                                            </span>
                                            <span>{sub.label || subKey}</span>
                                            {subVal.comments && <span style={{ color: '#6b7280', fontStyle: 'italic' }}> — {subVal.comments}</span>}
                                        </div>
                                    );
                                }) : <span>No checklist items</span>}
                                {isEditingTemplateData ? (
                                    <textarea
                                        className='details-edit-textarea'
                                        style={{ marginTop: '6px' }}
                                        placeholder='Group comments…'
                                        value={groupValue._comments || ''}
                                        onChange={(e) => {
                                            const updated = { ...groupValue, _comments: e.target.value };
                                            setEditingDataJson(prev => ({ ...prev, [field.name]: updated }));
                                        }}
                                        rows={2}
                                    />
                                ) : groupValue._comments ? (
                                    <div style={{ marginTop: '6px', paddingTop: '4px', borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: '0.85rem' }}>
                                        <em>Comments:</em> {groupValue._comments}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    );
                }

                // ── Checklist Item (standalone) ─────────────────────────────
                if (field.type === 'checklist-item') {
                    const savedVal = typeof fieldVal === 'object' && fieldVal !== null ? fieldVal : { checked: false, comments: '' };
                    const checkVal = isEditingTemplateData
                        ? (typeof editingDataJson[field.name] === 'object' && editingDataJson[field.name] !== null
                            ? editingDataJson[field.name]
                            : savedVal)
                        : savedVal;
                    const isChecked = checkVal.checked === true || checkVal.checked === 'true';

                    if (isEditingTemplateData) {
                        return (
                            <div className='form-data-row' key={field.name}>
                                <span className='form-data-label'>{field.label || field.name}:</span>
                                <span className='form-data-value' style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input
                                            type='checkbox'
                                            checked={isChecked}
                                            onChange={(e) => {
                                                const updated = { ...checkVal, checked: e.target.checked };
                                                setEditingDataJson(prev => ({ ...prev, [field.name]: updated }));
                                            }}
                                        />
                                        <span>{isChecked ? 'Yes' : 'No'}</span>
                                    </label>
                                    <input
                                        type='text'
                                        className='details-edit-input'
                                        style={{ flex: 1, minWidth: '120px', fontSize: '0.85rem' }}
                                        placeholder='Comments…'
                                        value={checkVal.comments || ''}
                                        onChange={(e) => {
                                            const updated = { ...checkVal, comments: e.target.value };
                                            setEditingDataJson(prev => ({ ...prev, [field.name]: updated }));
                                        }}
                                    />
                                </span>
                            </div>
                        );
                    }

                    return (
                        <div className='form-data-row' key={field.name}>
                            <span className='form-data-label'>{field.label || field.name}:</span>
                            <span className='form-data-value'>
                                <span className={`checkbox-answer ${isChecked ? 'checked' : 'unchecked'}`}>
                                    {isChecked ? '✓ Yes' : '✗ No'}
                                </span>
                                {checkVal.comments && <span style={{ color: '#6b7280', fontStyle: 'italic' }}> — {checkVal.comments}</span>}
                            </span>
                        </div>
                    );
                }


                // Editing mode — render inputs
                if (isEditingTemplateData) {
                    const editVal = editingDataJson[field.name] ?? rawVal ?? '';
                    return (
                        <div className='form-data-row' key={field.name}>
                            <span className='form-data-label'>{field.label || field.name}:</span>
                            <span className='form-data-value'>
                                {!canInlineEditField ? (
                                    <span className='details-readonly-value'>
                                        {formatInlineReadOnlyFieldValue(fieldVal)}
                                    </span>
                                ) : field.type === 'date' ? (
                                    <input
                                        type='date'
                                        className='details-edit-input'
                                        value={typeof editVal === 'string' && editVal.includes('T') ? editVal.split('T')[0] : editVal}
                                        onChange={(e) => setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }))}
                                    />
                                ) : field.type === 'textarea' ? (
                                    <textarea
                                        className='details-edit-textarea'
                                        value={typeof editVal === 'object' ? JSON.stringify(editVal) : editVal}
                                        onChange={(e) => setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }))}
                                        rows={3}
                                    />
                                ) : field.type === 'select' && field.options ? (
                                    <select
                                        className='details-edit-select'
                                        value={typeof editVal === 'object' ? '' : editVal}
                                        onChange={(e) => setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }))}
                                    >
                                        <option value=''>-- Select --</option>
                                        {field.options.map(opt => (
                                            <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
                                        ))}
                                    </select>
                                ) : field.type === 'entra-user-selector' ? (
                                    <div className='entra-user-selector-field' style={{ position: 'relative' }}>
                                        <input
                                            type='text'
                                            className='details-edit-input'
                                            value={typeof editVal === 'object' ? (editVal.displayName || editVal.name || '') : (editVal || '')}
                                            onChange={(e) => {
                                                const text = e.target.value;
                                                setEditingDataJson(prev => ({ ...prev, [field.name]: text }));
                                                // Debounced Entra search
                                                if (entraEditTimers.current[field.name]) clearTimeout(entraEditTimers.current[field.name]);
                                                if (text.length < 2) {
                                                    setEntraEditSearchResults(prev => ({ ...prev, [field.name]: [] }));
                                                    setEntraEditDropdownOpen(prev => ({ ...prev, [field.name]: false }));
                                                    return;
                                                }
                                                entraEditTimers.current[field.name] = setTimeout(async () => {
                                                    setEntraEditSearchLoading(prev => ({ ...prev, [field.name]: true }));
                                                    try {
                                                        const res = await apiFetch(`/api/Entra/users/search?q=${encodeURIComponent(text)}&top=8`);
                                                        const data = await res.json();
                                                        if (data.success) {
                                                            setEntraEditSearchResults(prev => ({ ...prev, [field.name]: data.data || [] }));
                                                            setEntraEditDropdownOpen(prev => ({ ...prev, [field.name]: true }));
                                                        }
                                                    } catch (err) {
                                                        Sentry.captureException(err);
                                                    } finally {
                                                        setEntraEditSearchLoading(prev => ({ ...prev, [field.name]: false }));
                                                    }
                                                }, 350);
                                            }}
                                            placeholder={field.placeholder || 'Search by name or email...'}
                                            autoComplete='off'
                                        />
                                        {entraEditSearchLoading[field.name] && (
                                            <small style={{ color: '#6b7280' }}>Searching Microsoft 365…</small>
                                        )}
                                        {entraEditDropdownOpen[field.name] && (entraEditSearchResults[field.name] || []).length > 0 && (
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, right: 0,
                                                background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                                                zIndex: 20, maxHeight: 220, overflowY: 'auto',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                            }}>
                                                {entraEditSearchResults[field.name].map(u => (
                                                    <button
                                                        type='button'
                                                        key={u.id}
                                                        style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', width: '100%', textAlign: 'left', border: 'none', background: 'transparent' }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                        onClick={async () => {
                                                            // Set the display name as the field value
                                                            setEditingDataJson(prev => ({ ...prev, [field.name]: u.displayName || u.name || '' }));
                                                            setEditingDataJson(prev => ({ ...prev, [`${field.name}_entraId`]: u.id || '' }));
                                                            setEntraEditDropdownOpen(prev => ({ ...prev, [field.name]: false }));
                                                            // Fetch full details and auto-populate linked fields
                                                            if (field.autoPopulate && u.id) {
                                                                try {
                                                                    const detailsRes = await apiFetch(`/api/Entra/users/${u.id}/details`);
                                                                    const detailsData = await detailsRes.json();
                                                                    if (detailsData.success && detailsData.data) {
                                                                        const details = detailsData.data;
                                                                        const updates = {};
                                                                        Object.entries(field.autoPopulate).forEach(([sourceKey, targetFieldName]) => {
                                                                            if (details[sourceKey] !== undefined && details[sourceKey] !== null) {
                                                                                updates[targetFieldName] = String(details[sourceKey]);
                                                                            }
                                                                        });
                                                                        if (Object.keys(updates).length > 0) {
                                                                            setEditingDataJson(prev => ({ ...prev, ...updates }));
                                                                        }
                                                                    }
                                                                } catch (err) {
                                                                    Sentry.captureException(err);
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 500 }}>{u.displayName}</div>
                                                        <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                            {u.jobTitle && <span>{u.jobTitle} · </span>}
                                                            {u.department && <span>{u.department} · </span>}
                                                            {u.email && <span>{u.email}</span>}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : field.type === 'property-selector' ? (
                                    <div className='property-selector-field'>
                                        <select
                                            className='details-edit-select'
                                            value={typeof editVal === 'object' ? '' : editVal}
                                            onChange={(e) => {
                                                const newPropId = e.target.value;
                                                setEditingDataJson(prev => ({ ...prev, [field.name]: newPropId }));
                                                // Clear dependent tenant/shop fields when property changes
                                                templateFields.forEach(f => {
                                                    if ((f.type === 'tenant-selector' || f.type === 'shop-number') && f.dependsOnField === field.name) {
                                                        setEditingDataJson(prev => ({ ...prev, [f.name]: '' }));
                                                        setEditShopResults(prev => ({ ...prev, [f.name]: [] }));
                                                    }
                                                });
                                                // Pre-load shops for dependent shop-number fields
                                                if (newPropId) {
                                                    templateFields.forEach(f => {
                                                        if (f.type === 'shop-number' && f.dependsOnField === field.name) {
                                                            loadEditShopsForField(f.name, parseInt(newPropId, 10));
                                                        }
                                                    });
                                                }
                                            }}
                                        >
                                            <option value=''>-- Select Property --</option>
                                            {properties.map(p => (
                                                <option key={p.propertyID} value={p.propertyID}>
                                                    {p.propertyName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : field.type === 'tenant-selector' ? (
                                    (() => {
                                        const linkedPropId = resolveEditLinkedPropertyId(field);
                                        const tenants = editTenantResults[field.name] || [];
                                        const isSearching = editTenantLoading[field.name] || false;
                                        const dropdownOpen = editTenantDropdownOpen[field.name] || false;
                                        return (
                                            <div className='tenant-selector-field' style={{ position: 'relative' }}>
                                                {!linkedPropId && (
                                                    <small style={{ color: '#6b7280' }}>Select a property first.</small>
                                                )}
                                                <input
                                                    type='text'
                                                    className='details-edit-input'
                                                    value={typeof editVal === 'object' ? '' : (editVal || '')}
                                                    onChange={(e) => {
                                                        setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }));
                                                        searchEditTenantsForField(field.name, linkedPropId, e.target.value);
                                                    }}
                                                    disabled={!linkedPropId}
                                                    placeholder={linkedPropId ? 'Search tenant name…' : 'Select a property first'}
                                                    autoComplete='off'
                                                />
                                                {isSearching && <small style={{ color: '#6b7280' }}>Searching…</small>}
                                                {dropdownOpen && tenants.length > 0 && (
                                                    <div style={{
                                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                                        background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                                                        zIndex: 20, maxHeight: 220, overflowY: 'auto',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                                    }}>
                                                        {tenants.map(t => (
                                                            <button
                                                                type='button'
                                                                key={t.tenantID}
                                                                style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', width: '100%', textAlign: 'left', border: 'none', background: 'transparent' }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                                onClick={async () => {
                                                                    const display = t.listOrTradingAsName || t.lesseeName || `Tenant ${t.tenantID}`;
                                                                    setEditingDataJson(prev => ({ ...prev, [field.name]: display, [`${field.name}_tenantId`]: String(t.tenantID) }));
                                                                    setEditTenantDropdownOpen(prev => ({ ...prev, [field.name]: false }));
                                                                    // Auto-populate linked fields
                                                                    if (field.autoPopulate) {
                                                                        const tenantDataMap = {
                                                                            contactName: t.contactName || t.generalContactName || '',
                                                                            contactPhone: t.contactPhone || t.generalContactTelephoneNos || '',
                                                                            contactEmail: t.contactEmail || t.generalContactEmailNos || '',
                                                                            shopNumber: t.mainUnitNo || '',
                                                                            tenantCode: t.tenantCode || '',
                                                                            lesseeName: t.lesseeName || '',
                                                                            tradingName: t.listOrTradingAsName || ''
                                                                        };
                                                                        const updates = {};
                                                                        Object.entries(field.autoPopulate).forEach(([sourceKey, targetFieldName]) => {
                                                                            if (tenantDataMap[sourceKey] !== undefined && tenantDataMap[sourceKey] !== '') {
                                                                                updates[targetFieldName] = tenantDataMap[sourceKey];
                                                                            }
                                                                        });
                                                                        if (Object.keys(updates).length > 0) {
                                                                            setEditingDataJson(prev => ({ ...prev, ...updates }));
                                                                        }
                                                                    }
                                                                    // Auto-set shop via unit-for-tenant
                                                                    if (linkedPropId && t.tenantID) {
                                                                        try {
                                                                            const res = await apiFetch(`/api/AssetManagement/mda/unit-for-tenant?propertyId=${linkedPropId}&tenantId=${t.tenantID}`);
                                                                            const data = await res.json();
                                                                            if (data.success && data.data) {
                                                                                templateFields.forEach(f => {
                                                                                    if (f.type === 'shop-number' && f.dependsOnField === field.dependsOnField) {
                                                                                        setEditingDataJson(prev => ({ ...prev, [f.name]: data.data.propertyRentableUnitNo || '' }));
                                                                                    }
                                                                                });
                                                                            }
                                                                        } catch (err) { Sentry.captureException(err); }
                                                                    }
                                                                }}
                                                            >
                                                                <div style={{ fontWeight: 500 }}>{t.listOrTradingAsName || t.lesseeName}</div>
                                                                <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                                    {t.mainUnitNo && <span>Shop {t.mainUnitNo} · </span>}
                                                                    {t.contactName && <span>{t.contactName}</span>}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()
                                ) : field.type === 'shop-number' ? (
                                    (() => {
                                        const shopPropId = resolveEditLinkedPropertyId(field);
                                        const shops = editShopResults[field.name] || [];
                                        const shopsLoading = editShopLoading[field.name] || false;
                                        const shopSearch = editShopSearchText[field.name] || '';
                                        const shopDropdownVisible = editShopDropdownOpen[field.name] || false;
                                        const filteredShops = shopSearch
                                            ? shops.filter(u => {
                                                const unitNo = (u.propertyRentableUnitNo || u.unitNo || '').toLowerCase();
                                                const desc = (u.description || '').toLowerCase();
                                                return unitNo.includes(shopSearch.toLowerCase()) || desc.includes(shopSearch.toLowerCase());
                                            })
                                            : shops;
                                        return (
                                            <div className='shop-number-field' style={{ position: 'relative' }}>
                                                {!shopPropId && <small style={{ color: '#6b7280' }}>Select a property first.</small>}
                                                {shopsLoading ? (
                                                    <small style={{ color: '#6b7280' }}>Loading units…</small>
                                                ) : (
                                                    <>
                                                        <input
                                                            type='text'
                                                            className='details-edit-input'
                                                            value={editVal || shopSearch}
                                                            onChange={(e) => {
                                                                const text = e.target.value;
                                                                if (editVal) setEditingDataJson(prev => ({ ...prev, [field.name]: '' }));
                                                                setEditShopSearchText(prev => ({ ...prev, [field.name]: text }));
                                                                setEditShopDropdownOpen(prev => ({ ...prev, [field.name]: true }));
                                                            }}
                                                            onFocus={() => {
                                                                if (shopPropId && !editVal) {
                                                                    setEditShopDropdownOpen(prev => ({ ...prev, [field.name]: true }));
                                                                }
                                                            }}
                                                            disabled={!shopPropId}
                                                            placeholder={shopPropId ? 'Search shop/unit…' : 'Select a property first'}
                                                            autoComplete='off'
                                                        />
                                                        {shopDropdownVisible && filteredShops.length > 0 && (
                                                            <div style={{
                                                                position: 'absolute', top: '100%', left: 0, right: 0,
                                                                background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                                                                zIndex: 20, maxHeight: 220, overflowY: 'auto',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                                            }}>
                                                                {filteredShops.map(u => (
                                                                    <button
                                                                        type='button'
                                                                        key={u.propertyRentableUnitID || u.id}
                                                                        style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', width: '100%', textAlign: 'left', border: 'none', background: 'transparent' }}
                                                                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                                        onClick={async () => {
                                                                            const unitNo = u.propertyRentableUnitNo || u.unitNo || '';
                                                                            setEditingDataJson(prev => ({ ...prev, [field.name]: unitNo }));
                                                                            setEditShopSearchText(prev => ({ ...prev, [field.name]: '' }));
                                                                            setEditShopDropdownOpen(prev => ({ ...prev, [field.name]: false }));
                                                                            // Auto-populate from shop → tenant reverse lookup
                                                                            if (shopPropId && u.propertyRentableUnitID) {
                                                                                try {
                                                                                    const res = await apiFetch(`/api/AssetManagement/mda/tenant-for-unit?propertyId=${shopPropId}&rentableUnitId=${u.propertyRentableUnitID}`);
                                                                                    const data = await res.json();
                                                                                    if (data.success && data.data) {
                                                                                        const tenant = data.data;
                                                                                        // Auto-fill tenant-selector fields
                                                                                        templateFields.forEach(f => {
                                                                                            if (f.type === 'tenant-selector' && f.dependsOnField === field.dependsOnField) {
                                                                                                const display = tenant.listOrTradingAsName || tenant.lesseeName || '';
                                                                                                setEditingDataJson(prev => ({ ...prev, [f.name]: display, [`${f.name}_tenantId`]: String(tenant.tenantID) }));
                                                                                                // Auto-populate tenant's linked fields
                                                                                                if (f.autoPopulate) {
                                                                                                    const tenantDataMap = {
                                                                                                        contactName: tenant.contactName || '',
                                                                                                        contactPhone: tenant.contactPhone || '',
                                                                                                        contactEmail: tenant.contactEmail || '',
                                                                                                        shopNumber: tenant.mainUnitNo || '',
                                                                                                        tenantCode: tenant.tenantCode || '',
                                                                                                        lesseeName: tenant.lesseeName || '',
                                                                                                        tradingName: tenant.listOrTradingAsName || ''
                                                                                                    };
                                                                                                    const updates = {};
                                                                                                    Object.entries(f.autoPopulate).forEach(([sourceKey, targetFieldName]) => {
                                                                                                        if (tenantDataMap[sourceKey] !== undefined && tenantDataMap[sourceKey] !== '') {
                                                                                                            updates[targetFieldName] = tenantDataMap[sourceKey];
                                                                                                        }
                                                                                                    });
                                                                                                    if (Object.keys(updates).length > 0) {
                                                                                                        setEditingDataJson(prev => ({ ...prev, ...updates }));
                                                                                                    }
                                                                                                }
                                                                                            }
                                                                                        });
                                                                                        // Apply shop-number's own autoPopulate
                                                                                        if (field.autoPopulate) {
                                                                                            const shopDataMap = {
                                                                                                contactName: tenant.contactName || '',
                                                                                                contactPhone: tenant.contactPhone || '',
                                                                                                contactEmail: tenant.contactEmail || '',
                                                                                                tenantName: tenant.listOrTradingAsName || tenant.lesseeName || '',
                                                                                                tenantCode: tenant.tenantCode || '',
                                                                                                lesseeName: tenant.lesseeName || ''
                                                                                            };
                                                                                            const updates = {};
                                                                                            Object.entries(field.autoPopulate).forEach(([sourceKey, targetFieldName]) => {
                                                                                                if (shopDataMap[sourceKey] !== undefined && shopDataMap[sourceKey] !== '') {
                                                                                                    updates[targetFieldName] = shopDataMap[sourceKey];
                                                                                                }
                                                                                            });
                                                                                            if (Object.keys(updates).length > 0) {
                                                                                                setEditingDataJson(prev => ({ ...prev, ...updates }));
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                } catch (err) { Sentry.captureException(err); }
                                                                            }
                                                                        }}
                                                                    >
                                                                        <div style={{ fontWeight: 500 }}>{u.propertyRentableUnitNo || u.unitNo}</div>
                                                                        {u.description && <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>{u.description}</div>}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })()
                                ) : field.type === 'role-selector' ? (
                                    <div className='role-selector-field'>
                                        {editRoleLoading ? (
                                            <small style={{ color: '#6b7280' }}>Loading roles…</small>
                                        ) : (
                                            <select
                                                className='details-edit-select'
                                                value={typeof editVal === 'object' ? '' : editVal}
                                                onChange={(e) => setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }))}
                                            >
                                                <option value=''>-- Select Role --</option>
                                                {editRoleOptions.map(r => (
                                                    <option key={r.roleID} value={r.roleName}>
                                                        {r.roleName}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                ) : field.type === 'lookup-selector' ? (
                                    <div className='lookup-selector-field' style={{ position: 'relative' }}>
                                        {field.lookupSearchable ? (
                                            <>
                                                <input
                                                    type='text'
                                                    className='details-edit-input'
                                                    value={typeof editVal === 'object' ? '' : (editVal || '')}
                                                    onChange={(e) => {
                                                        setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }));
                                                        fetchEditLookupData(field, e.target.value);
                                                    }}
                                                    placeholder={field.placeholder || 'Type to search...'}
                                                    autoComplete='off'
                                                />
                                                {editLookupLoading[field.name] && (
                                                    <small style={{ color: '#6b7280' }}>Searching…</small>
                                                )}
                                                {(editLookupResults[field.name] || []).length > 0 && (
                                                    <div style={{
                                                        position: 'absolute', top: '100%', left: 0, right: 0,
                                                        background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                                                        zIndex: 20, maxHeight: 220, overflowY: 'auto',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                                    }}>
                                                        {editLookupResults[field.name].map((item, idx) => (
                                                            <button
                                                                type='button'
                                                                key={item[field.lookupValueField] || idx}
                                                                style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.8rem', width: '100%', textAlign: 'left', border: 'none', background: 'transparent' }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                                onClick={() => {
                                                                    setEditingDataJson(prev => ({ ...prev, [field.name]: String(item[field.lookupValueField] || item[field.lookupDisplayField] || '') }));
                                                                    setEditLookupResults(prev => ({ ...prev, [field.name]: [] }));
                                                                }}
                                                            >
                                                                {item[field.lookupDisplayField] || JSON.stringify(item)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            editLookupLoading[field.name] ? (
                                                <small style={{ color: '#6b7280' }}>Loading…</small>
                                            ) : (
                                                <select
                                                    className='details-edit-select'
                                                    value={typeof editVal === 'object' ? '' : (editVal || '')}
                                                    onChange={(e) => setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }))}
                                                >
                                                    <option value=''>-- Select --</option>
                                                    {(editLookupResults[field.name] || []).map((item, idx) => (
                                                        <option key={item[field.lookupValueField] || idx} value={item[field.lookupValueField] || ''}>
                                                            {item[field.lookupDisplayField] || JSON.stringify(item)}
                                                        </option>
                                                    ))}
                                                </select>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    <input
                                        type='text'
                                        className='details-edit-input'
                                        value={typeof editVal === 'object' ? (editVal.displayName || editVal.name || '') : editVal}
                                        onChange={(e) => setEditingDataJson(prev => ({ ...prev, [field.name]: e.target.value }))}
                                    />
                                )}
                            </span>
                        </div>
                    );
                }

                return (
                    <div className='form-data-row' key={field.name}>
                        <span className='form-data-label'>{field.label || field.name}:</span>
                        <span className='form-data-value'>
                            {isSignatureImage ? (
                                <img
                                    src={fieldVal}
                                    alt="Signature"
                                    style={{ maxWidth: '300px', maxHeight: '100px', objectFit: 'contain', display: 'block' }}
                                />
                            ) : (
                                typeof fieldVal === 'object' ? (fieldVal?.displayName || fieldVal?.name || 'N/A') : fieldVal
                            )}
                        </span>
                    </div>
                );
            };

            // Flatten row containers into their leaf fields for rendering
            const flattenSectionFields = (fields = []) => fields.flatMap(field => {
                if (field?.type === 'row' && Array.isArray(field.columns)) {
                    return field.columns.flatMap(col => col?.fields || []);
                }
                return field;
            });

            const fetchEditLookupData = (field, searchText) => {
                const fieldName = field.name;
                if (editLookupTimers.current[fieldName]) clearTimeout(editLookupTimers.current[fieldName]);
                editLookupTimers.current[fieldName] = setTimeout(async () => {
                    if (!field.lookupEndpoint) return;
                    setEditLookupLoading(prev => ({ ...prev, [fieldName]: true }));
                    try {
                        let url = field.lookupEndpoint;
                        const params = new URLSearchParams();
                        if (searchText) params.set('search', searchText);
                        if (field.lookupDependsOn && field.lookupQueryParam) {
                            const dependentValue = editingDataJson[field.lookupDependsOn] ?? getFieldValue(field.lookupDependsOn);
                            if (dependentValue !== undefined && dependentValue !== null && dependentValue !== '') {
                                params.set(field.lookupQueryParam, String(dependentValue));
                            }
                        }
                        const qs = params.toString();
                        if (qs) url += (url.includes('?') ? '&' : '?') + qs;
                        const response = await apiFetch(url);
                        if (response.ok) {
                            const data = await response.json();
                            setEditLookupResults(prev => ({ ...prev, [fieldName]: Array.isArray(data) ? data : [] }));
                        }
                    } catch (err) {
                        Sentry.captureException(err);
                    } finally {
                        setEditLookupLoading(prev => ({ ...prev, [fieldName]: false }));
                    }
                }, 300);
            };

            /**
             * Resolve the linked property ID for a tenant-selector or shop-number field.
             * Checks the editingDataJson for the value of the dependsOnField,
             * falls back to the action item's propertyID.
             */
            const resolveEditLinkedPropertyId = (fieldDef) => {
                const linkedFieldName = fieldDef.dependsOnField;
                if (linkedFieldName === '__actionItemProperty__') {
                    return actionItem?.actionItem?.propertyID || null;
                }
                if (linkedFieldName) {
                    const val = editingDataJson[linkedFieldName];
                    return val ? parseInt(val, 10) || null : null;
                }
                // Fallback to action item property
                return actionItem?.actionItem?.propertyID || null;
            };

            /**
             * Debounced tenant search for tenant-selector in edit mode.
             */
            const searchEditTenantsForField = (fieldName, propertyId, searchText) => {
                if (editTenantTimers.current[fieldName]) clearTimeout(editTenantTimers.current[fieldName]);
                if (!propertyId || !searchText || searchText.length < 2) {
                    setEditTenantResults(prev => ({ ...prev, [fieldName]: [] }));
                    return;
                }
                editTenantTimers.current[fieldName] = setTimeout(async () => {
                    setEditTenantLoading(prev => ({ ...prev, [fieldName]: true }));
                    try {
                        const res = await apiFetch(`/api/AssetManagement/mda/tenants?propertyId=${propertyId}&search=${encodeURIComponent(searchText)}`);
                        const data = await res.json();
                        if (data.success) {
                            setEditTenantResults(prev => ({ ...prev, [fieldName]: data.data || [] }));
                            setEditTenantDropdownOpen(prev => ({ ...prev, [fieldName]: true }));
                        }
                    } catch (err) {
                        Sentry.captureException(err);
                        setEditTenantResults(prev => ({ ...prev, [fieldName]: [] }));
                    } finally {
                        setEditTenantLoading(prev => ({ ...prev, [fieldName]: false }));
                    }
                }, 300);
            };

            /**
             * Load rentable units (shops) for a shop-number field in edit mode.
             */
            const loadEditShopsForField = async (fieldName, propertyId) => {
                if (!propertyId) {
                    setEditShopResults(prev => ({ ...prev, [fieldName]: [] }));
                    return;
                }
                setEditShopLoading(prev => ({ ...prev, [fieldName]: true }));
                try {
                    const res = await apiFetch(`/api/AssetManagement/mda/rentable-units?propertyId=${propertyId}`);
                    const data = await res.json();
                    if (data.success) {
                        setEditShopResults(prev => ({ ...prev, [fieldName]: data.data || [] }));
                    }
                } catch (err) {
                    Sentry.captureException(err);
                    setEditShopResults(prev => ({ ...prev, [fieldName]: [] }));
                } finally {
                    setEditShopLoading(prev => ({ ...prev, [fieldName]: false }));
                }
            };

            const handleStartEditTemplateData = () => {
                const ai = actionItem?.actionItem;
                let data = {};
                if (ai?.dataJson) {
                    try {
                        data = typeof ai.dataJson === 'string' ? JSON.parse(ai.dataJson) : ai.dataJson;
                    } catch { /* ignore */ }
                }
                setEditingDataJson(data);
                setIsEditingTemplateData(true);

                // Fetch roles if any role-selector fields exist
                const hasRoleField = templateFields.some(f => f.type === 'role-selector');
                if (hasRoleField && editRoleOptions.length === 0) {
                    setEditRoleLoading(true);
                    apiFetch('/api/Entra/roles')
                        .then(r => r.json())
                        .then(d => { if (d.success) setEditRoleOptions(d.data || []); })
                        .catch(e => Sentry.captureException(e))
                        .finally(() => setEditRoleLoading(false));
                }

                // Fetch initial data for non-searchable lookup-selector fields
                const staticLookups = templateFields.filter(f => f.type === 'lookup-selector' && !f.lookupSearchable && f.lookupEndpoint);
                staticLookups.forEach(f => fetchEditLookupData(f, ''));

                // Pre-load shops for shop-number fields that have a resolved property
                templateFields.filter(f => f.type === 'shop-number').forEach(f => {
                    const linkedFieldName = f.dependsOnField;
                    const propId = linkedFieldName === '__actionItemProperty__'
                        ? (ai?.propertyID || null)
                        : (linkedFieldName && data[linkedFieldName]
                            ? (parseInt(data[linkedFieldName], 10) || null)
                            : (ai?.propertyID || null));
                    if (propId) loadEditShopsForField(f.name, propId);
                });
            };

            const handleSaveTemplateData = async () => {
                setIsSavingTemplateData(true);
                try {
                    const existingActionItemState = actionItem?.actionItem || {};
                    const response = await apiFetch(`/api/ActionItems/${id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            dataJson: JSON.stringify(editingDataJson),
                            assignedToUserID: existingActionItemState.assignedToUserID ?? null,
                            milestoneId: existingActionItemState.milestoneId ?? null,
                            parentActionItemId: existingActionItemState.parentActionItemId ?? null,
                            userId: user.userID
                        })
                    });
                    const data = await response.json();
                    if (data.success) {
                        toast.success('Form data updated.');
                        setIsEditingTemplateData(false);
                        fetchActionItem();
                    } else {
                        toast.error(data.message || 'Failed to update form data.');
                    }
                } catch (err) {
                    Sentry.captureException(err);
                    toast.error('An error occurred while saving form data.');
                } finally {
                    setIsSavingTemplateData(false);
                }
            };

            return (
                <div className='submitted-form-section'>
                    <div className='details-section-header'>
                        <h2>{currentTemplate.displayName || 'Form Details'}</h2>
                        {!isEditingTemplateData ? (
                            <button
                                type="button"
                                className='btn-edit-details'
                                onClick={handleStartEditTemplateData}
                                title="Edit form data"
                            >
                                <FaEdit size={14} /> Edit
                            </button>
                        ) : (
                            <div className='details-edit-actions'>
                                <button
                                    type="button"
                                    className='btn-save-details'
                                    onClick={handleSaveTemplateData}
                                    disabled={isSavingTemplateData}
                                    title="Save changes"
                                >
                                    {isSavingTemplateData ? 'Saving…' : <><FaCheck size={12} /> Save</>}
                                </button>
                                <button
                                    type="button"
                                    className='btn-cancel-details'
                                    onClick={() => setIsEditingTemplateData(false)}
                                    disabled={isSavingTemplateData}
                                    title="Discard changes"
                                >
                                    <FaTimes size={12} /> Cancel
                                </button>
                            </div>
                        )}
                    </div>
                    <div className='form-data-display'>
                        {/* Always show Property first */}
                        <div className='form-data-row'>
                            <span className='form-data-label'>Property:</span>
                            <span className='form-data-value'>{propertyName || 'N/A'}</span>
                        </div>

                        {/* Render with section groupings if available */}
                        {sections.length > 0 ? (
                            sections.map((section, idx) => (
                                <div key={section.id || idx} className='form-data-section-group'>
                                    {section.title && <h3 className='form-data-section-title'>{section.title}</h3>}
                                    {flattenSectionFields(section.fields || []).map(renderFieldRow)}
                                </div>
                            ))
                        ) : (
                            templateFields.map(renderFieldRow)
                        )}

                        {/* Always show Priority at the end */}
                        <div className='form-data-row'>
                            <span className='form-data-label'>Priority:</span>
                            <span className={`form-data-value priority-${(actionItem.actionItem.priority || 'low').toLowerCase()}`}>
                                {actionItem.actionItem.priority || 'Low'}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }
        
        // Fallback: Non-Compliance default rendering when no template schema is available
        if (isNonCompliance) {
            return (
                <div className='submitted-form-section'>
                    <h2>Non-Compliance Form Details</h2>
                    <div className='form-data-display'>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Property:</span>
                            <span className='form-data-value'>{propertyName || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Shop Number:</span>
                            <span className='form-data-value'>{actionItem.actionItem.shopNumber || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Tenant:</span>
                            <span className='form-data-value'>{actionItem.actionItem.tenant || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Non-Compliance Type:</span>
                            <span className='form-data-value'>{actionItem.actionItem.description || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Tenant Response:</span>
                            <span className='form-data-value'>{actionItem.actionItem.tenantResponse || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
                            <span className='form-data-label'>Initial Comments:</span>
                            <span className='form-data-value'>{actionItem.actionItem.comments || 'N/A'}</span>
                        </div>
                        <div className='form-data-row'>
      <span className='form-data-label'>Contact Name:</span>
      <span className='form-data-value'>{actionItem.actionItem.tenantContactName || 'N/A'}</span>
    </div>
    <div className='form-data-row'>
      <span className='form-data-label'>Contact Number:</span>
      <span className='form-data-value'>{actionItem.actionItem.tenantContactNumber || 'N/A'}</span>
    </div>
    {actionItem.actionItem.tenantContactEmail && (
      <div className='form-data-row'>
        <span className='form-data-label'>Contact Email:</span>
        <span className='form-data-value'>{actionItem.actionItem.tenantContactEmail}</span>
      </div>
    )}
    <div className='form-data-row'>
      <span className='form-data-label'>Priority:</span>
      <span className={`form-data-value priority-${(actionItem.actionItem.priority || 'low').toLowerCase()}`}>
        {actionItem.actionItem.priority || 'Low'}
      </span>
    </div>
                    </div>
                </div>
            );
        }
        
        // No template and not non-compliance - check if there's any form data to show
        const ai = actionItem?.actionItem;
        if (ai && (ai.title || ai.description || ai.dataJson)) {
            return (
                <div className='submitted-form-section'>
                    {/* ── Section header with optional Edit / Save / Cancel controls ── */}
                    <div className='details-section-header'>
                        <h2>Action Item Details</h2>
                        {!isEditingDetails ? (
                            <button
                                type="button"
                                className='btn-edit-details'
                                onClick={handleStartEditDetails}
                                title="Edit action item details"
                                aria-label="Edit action item details"
                            >
                                <FaEdit size={14} /> Edit
                            </button>
                        ) : (
                            <div className='details-edit-actions'>
                                <button
                                    type="button"
                                    className='btn-save-details'
                                    onClick={handleSaveDetails}
                                    disabled={isSavingDetails}
                                    title="Save changes"
                                >
                                    {isSavingDetails ? 'Saving…' : <><FaCheck size={12} /> Save</>}
                                </button>
                                <button
                                    type="button"
                                    className='btn-cancel-details'
                                    onClick={handleCancelEditDetails}
                                    disabled={isSavingDetails}
                                    title="Discard changes"
                                >
                                    <FaTimes size={12} /> Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    <div className='form-data-display'>
                        {/* Property — editable */}
                        <div className='form-data-row'>
                            <span className='form-data-label'>Property:</span>
                            {isEditingDetails ? (
                                <select
                                    className='details-edit-select'
                                    value={editDetailsPropertyId}
                                    onChange={(e) => setEditDetailsPropertyId(e.target.value)}
                                >
                                    <option value="">-- Select Property --</option>
                                    {properties.map(p => (
                                        <option key={p.propertyID} value={p.propertyID}>
                                            {p.propertyName}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <span className='form-data-value'>{propertyName || 'N/A'}</span>
                            )}
                        </div>

                        {/* Title — editable */}
                        <div className='form-data-row'>
                            <span className='form-data-label'>Title:</span>
                            {isEditingDetails ? (
                                <input
                                    type="text"
                                    className='details-edit-input'
                                    value={editDetailsTitle}
                                    onChange={(e) => setEditDetailsTitle(e.target.value)}
                                    maxLength={200}
                                    placeholder="Enter title…"
                                />
                            ) : (
                                <span className='form-data-value'>{ai.title || '—'}</span>
                            )}
                        </div>

                        {/* Description — editable */}
                        <div className='form-data-row form-data-row--column'>
                            <span className='form-data-label'>Description:</span>
                            {isEditingDetails ? (
                                <textarea
                                    className='details-edit-textarea'
                                    value={editDetailsDescription}
                                    onChange={(e) => setEditDetailsDescription(e.target.value)}
                                    rows={5}
                                    placeholder="Enter description…"
                                />
                            ) : (
                                <span className='form-data-value'>
                                    {ai.description ? renderDescription(ai.description) : '—'}
                                </span>
                            )}
                        </div>

                        {/* SIPA Report deep-link — visible for SIPA-sourced action items */}
                        {sipaReportLink && !isEditingDetails && (
                            <div className='form-data-row'>
                                <span className='form-data-label'>Source Report:</span>
                                <a
                                    href={`/sipa/report-capture/${sipaReportLink.reportId}#sub_${sipaReportLink.subSectionId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: '#1976d2', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                    title="Open the SIPA report and scroll to the related question"
                                >
                                    📋 View SIPA Report
                                </a>
                            </div>
                        )}

                        {/* Re-fetch email content button — only visible for email-sourced action items */}
                        {ai.source && ai.source.toLowerCase().includes('inboundemail') && !isEditingDetails && (
                            <div className='form-data-row email-refetch-row'>
                                <button
                                    type="button"
                                    className='btn-refetch-email'
                                    onClick={handleRefetchEmail}
                                    disabled={isRefetchingEmail}
                                    title="Re-fetch email content from the mailbox to resolve inline images"
                                >
                                    <FaSync className={isRefetchingEmail ? 'spin-icon' : ''} />
                                    {isRefetchingEmail ? ' Re-fetching…' : ' Re-fetch Email Content'}
                                </button>
                                {refetchResult && (
                                    <span className={`refetch-result refetch-result--${refetchResult.type}`}>
                                        {refetchResult.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                                        {' '}{refetchResult.message}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Initial Comments — intentionally read-only */}
                        {ai.comments && (
                            <div className='form-data-row'>
                                <span className='form-data-label'>Initial Comments:</span>
                                <span className='form-data-value'>{ai.comments}</span>
                            </div>
                        )}

                        {/* Priority — editable */}
                        <div className='form-data-row'>
                            <span className='form-data-label'>Priority:</span>
                            {isEditingDetails ? (
                                <select
                                    className='details-edit-select'
                                    value={editDetailsPriority}
                                    onChange={(e) => setEditDetailsPriority(e.target.value)}
                                >
                                    {PRIORITY_OPTIONS.map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            ) : (
                                <span className={`form-data-value priority-${(ai.priority || 'low').toLowerCase()}`}>
                                    {ai.priority || 'Low'}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        
        return null;
    };

    // Build dynamic title based on action item type
    const getDisplayTitle = () => {
        const ai = actionItem?.actionItem;
        if (!ai) return 'Action Item';
        
        // Get template for more specific title
        const currentTemplate = getCurrentTemplate();
        
        // For non-compliance items: "Non-compliance: {Tenant Name} at {Property}"
        if (isNonCompliance && ai.tenant && propertyName) {
            return `Non-compliance: ${ai.tenant} at ${propertyName}`;
        }
        
        // For other template-based items, use template display name
        if (currentTemplate && ai.title) {
            return `${currentTemplate.displayName}: ${ai.title}`;
        }
        
        // Default: use description or title
        return ai.title || ai.description || `Action Item #${ai.id}`;
    };

    /**
     * Get the icon for the current action item based on its template.
     * Looks up the template and returns the icon, falling back to a default.
     * @returns {string} FontAwesome icon class
     */
    const getItemIcon = () => {
        const ai = actionItem?.actionItem;
        if (!ai) return 'fa-clipboard-list';
        
        // Get current template
        const currentTemplate = getCurrentTemplate();
        
        // Return template icon if found
        if (currentTemplate && currentTemplate.icon) {
            return currentTemplate.icon;
        }
        
        // Default icons based on action item type
        if (ai.actionItemsType === 'Non-Compliance' || ai.templateKey === 'NonCompliance') {
            return 'fa-exclamation-triangle';
        }
        if (ai.actionItemsType === 'Manual' || ai.templateKey === 'Manual') {
            return 'fa-tasks';
        }
        
        // Generic default
        return 'fa-clipboard-list';
    };

    return (
        <div>
            {/* Page Header */}
            <div className='action-item-heading'>
                <h1>
                    <i className={`fas ${getItemIcon()} action-item-title-icon`}></i>
                    {getDisplayTitle()}
                    {/* Template type / source badge — provides instant visual context */}
                    {actionItem?.actionItem?.actionItemsType && (
                        <span
                            className='action-item-type-badge'
                            title={`Source: ${actionItem.actionItem.source || actionItem.actionItem.actionItemsType}`}
                        >
                            {actionItem.actionItem.actionItemsType}
                        </span>
                    )}
                    {/* Ticket reference badge for IT tickets */}
                    {actionItem?.actionItem?.ticketReference && (
                        <span className='action-item-ticket-ref' title='Ticket Reference'>
                            {actionItem.actionItem.ticketReference}
                        </span>
                    )}
                    {/* Status chip — mirrors the sidebar status at a glance */}
                    {status && (
                        <span
                            className='action-item-status-chip'
                            style={{ background: STATUS_COLORS[status] || '#718096' }}
                        >
                            {status}
                        </span>
                    )}
                </h1>
                {canAccessActionItemsPage && (
                    <div className="action-item-heading-actions">
                        <button onClick={() => navigate('/admin/action-items')}>Back</button>
                        <button
                            className="btn-delete-action-item"
                            onClick={() => setShowDeleteModal(true)}
                            title="Permanently delete this action item (Admin only)"
                        >
                            <FaTrashAlt /> Delete
                        </button>
                    </div>
                )}
            </div>

            {/* Main Layout: Form Data + Timeline + Sidebar */}
            <div className='action-item-detail-layout'>

                {/* ============================================================ */}
                {/* LEFT COLUMN: Form Data, Comments & Timeline                  */}
                {/* ============================================================ */}

                <div className='action-item-main'>

                    {/* ================================================ */}
                    {/* Merged Item Banner                                 */}
                    {/* Shown when this item was merged into another.     */}
                    {/* ================================================ */}
                    {mergedInfo && (
                        <div className='merged-item-banner'>
                            <FaCodeBranch size={16} className='merged-banner-icon' />
                            <span>
                                This action item was merged into{' '}
                                <strong>Action Item #{mergedInfo.targetId}</strong> and is now closed.
                            </span>
                            <button
                                className='merged-banner-link'
                                onClick={() => navigate(`/action-items/${mergedInfo.targetId}`)}
                                title={`Navigate to Action Item #${mergedInfo.targetId}`}
                            >
                                View Target Item →
                            </button>
                        </div>
                    )}

                    {/* ================================================ */}
                    {/* Parent Action Item Reference (if escalated from another item) */}
                    {/* ================================================ */}
                    {actionItem?.actionItem?.parentActionItemId && (
                <div className='parent-action-item-reference'>
                    <div className='reference-header'>
                        <FaLevelUpAlt size={16} />
                        <span>Escalated from Action Item</span>
                    </div>
                    <button 
                        className='reference-link'
                        onClick={() => navigate(`/action-items/${actionItem.actionItem.parentActionItemId}`)}
                    >
                        View Original Action Item #{actionItem.actionItem.parentActionItemId}
                    </button>
                </div>
            )}

            {/* ================================================ */}
            {/* Submitted Form Data Section - Dynamically rendered from templates */}
            {/* ================================================ */}
            {renderTemplateFormData()}

            {/* ================================================ */}
             {/* Daily Checklist Form Display - Read-only view of submitted checklist */}
             {/* Shows BELOW the regular action item details for DailyChecklist types */}
             {/* ================================================ */}
             {isDailyChecklist && getDailyChecklistData() && renderDailyChecklistFromData()}

            {/* ================================================ */}
            {/* File Attachments Section */}
            {/* ================================================ */}
            <AttachmentsSection
                attachments={attachments}
                loadingAttachments={loadingAttachments}
                uploadingFile={uploadingFile}
                fileInputRef={fileInputRef}
                onFileUpload={handleFileUpload}
                onDeleteAttachment={deleteAttachment}
                formatFileSize={formatFileSize}
                formatCommentDateTime={formatCommentDateTime}
                onDownloadAttachment={handleDownloadAttachment}
                onAnnotatePdf={(attachment) => {
                    setSelectedPdfAttachment(attachment);
                    setShowPdfAnnotationModal(true);
                }}
            />

                    {/* ================================================ */}
                    {/* Comments / History Two-Tab Panel                  */}
                    {/* Comments tab: markdown composer + comment log     */}
                    {/* History tab: all non-comment action events        */}
                    {/* ================================================ */}
                    <div className='activity-tabs-container'>

                        {/* Tab header row */}
                        <div className='activity-tabs-header'>
                            <button
                                className={`activity-tab-btn ${activeTab === 'comments' ? 'active' : ''}`}
                                onClick={() => setActiveTab('comments')}
                                title='Progress updates and discussion'
                            >
                                <FaCommentDots size={13} />
                                Comments
                                <span className='activity-tab-count'>{commentCount}</span>
                            </button>
                            <button
                                className={`activity-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                                onClick={() => setActiveTab('history')}
                                title='Status changes, assignments and other events'
                            >
                                <FaHistory size={13} />
                                History
                                <span className='activity-tab-count'>{historyCount}</span>
                            </button>

                            {/* IT Diagnostics tabs — only shown for IT ticket types
                                and users with router management permissions */}
                            {showItDiagnostics && (
                                <>
                                    <button
                                        className={`activity-tab-btn ${activeTab === 'endpoint' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('endpoint')}
                                        title='Endpoint diagnostics — device info, compliance, remote support'
                                    >
                                        <FaLaptop size={13} />
                                        Endpoint
                                        {itDiagnostics?.endpoint?.found && <span className='activity-tab-count diag-tab-dot'>●</span>}
                                    </button>
                                    <button
                                        className={`activity-tab-btn ${activeTab === 'network' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('network')}
                                        title='Network diagnostics — router, DHCP lease, network location'
                                    >
                                        <FaNetworkWired size={13} />
                                        Network
                                        {itDiagnostics?.network?.found && <span className='activity-tab-count diag-tab-dot'>●</span>}
                                    </button>
                                </>
                            )}

                            {/* Automations tab — shown for all action items that have a templateKey;
                                the tab component handles the case of no configured automations gracefully.
                                Shown last so it doesn't interrupt the core comments/history workflow. */}
                            {(actionItem?.actionItem?.templateKey || actionItem?.actionItem?.boardId) && (
                                <button
                                    className={`activity-tab-btn ${activeTab === 'automations' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('automations')}
                                    title='Workflow automations applicable to this action item'
                                >
                                    <FaBolt size={13} />
                                    Automations
                                    {automationsCount > 0 && (
                                        <span className='activity-tab-count'>{automationsCount}</span>
                                    )}
                                </button>
                            )}

                            {/* Tasks tab — shown when template has tasks defined */}
                            {templateTasks.length > 0 && (
                                <button
                                    className={`activity-tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('tasks')}
                                    title='Task checklists for this action item'
                                >
                                    <FaTasks size={13} />
                                    Tasks
                                    {templateTasks.reduce((s, g) => s + (g.items?.length || 0), 0) > 0 && (
                                        <span className='activity-tab-count'>
                                            {Object.values(taskValues).filter(v => v.checked).length}/
                                            {templateTasks.reduce((s, g) => s + (g.items?.length || 0), 0)}
                                        </span>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* ── Comments tab ─────────────────────────────── */}
                        {activeTab === 'comments' && (
                            <div className='activity-tab-panel'>

                                {/* Markdown-enabled comment composer */}
                                <CommentInputSection
                                    commentText={commentText}
                                    onCommentChange={(e) => {
                                        const val = e.target.value;
                                        if (typeof val === 'function') {
                                            setCommentText(prev => val(prev));
                                        } else {
                                            setCommentText(val);
                                        }
                                    }}
                                    onCommentSubmit={handleCommentSubmit}
                                    isSubmitting={isSubmittingComment}
                                    allUsers={allUsers}
                                    getUserDisplay={getUserDisplay}
                                    onFileAttach={uploadAttachment}
                                    onMentionSearch={handleMentionSearch}
                                    canAddInternalNotes={canAddInternalNotes}
                                    onInternalNoteSubmit={handleInternalNoteSubmit}
                                />

                                {/* Comment log entries — native comments only (merged comments
                                    are shown in their own grouped section below so they are
                                    clearly attributed to their source item) */}
                                <div className='action-item-log-list comment-log-list'>
                                    {nativeCommentLogs.length > 0
                                        ? nativeCommentLogs.map(renderLogRow)
                                        : (
                                            <div className='no-activity-msg'>
                                                No comments yet — add the first progress update above.
                                            </div>
                                        )
                                    }
                                </div>

                                {/* Imported conversations from merged items — grouped per
                                    source item so each conversation thread is traceable.
                                    Collapsed/expanded per group; source item ID is a link. */}
                                <MergedCommentsSection
                                    mergedCommentGroups={mergedCommentGroups}
                                    getUserName={getUserName}
                                    formatCommentDateTime={formatCommentDateTime}
                                />
                            </div>
                        )}

                        {/* ── History tab ──────────────────────────────── */}
                        {activeTab === 'history' && (
                            <div className='activity-tab-panel'>
                                <div className='action-item-log-list'>
                                    {historyLogs.length > 0
                                        ? historyLogs.map(renderLogRow)
                                        : (
                                            <div className='no-activity-msg'>
                                                No history events recorded yet.
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        )}

                        {/* ── Endpoint Diagnostics tab (IT tickets + router permissions) ── */}
                        {showItDiagnostics && activeTab === 'endpoint' && (
                            <div className='activity-tab-panel'>
                                {itDiagnosticsLoading ? (
                                    <div className='no-activity-msg'>Loading endpoint diagnostics...</div>
                                ) : (
                                    <EndpointDiagnosticsTab
                                        endpoint={itDiagnostics?.endpoint}
                                        requesterEmail={itDiagnostics?.requesterEmail}
                                        onRefresh={() => {
                                            setItDiagnostics(null);
                                            setItDiagnosticsLoading(true);
                                            apiFetch(`/api/ITDiagnostics/action-item/${actionItem.actionItem.id}`)
                                                .then(r => r.json())
                                                .then(d => { if (d.success) setItDiagnostics(d); })
                                                .catch(e => Sentry.captureException(e))
                                                .finally(() => setItDiagnosticsLoading(false));
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        {/* ── Network Diagnostics tab (IT tickets + router permissions) ── */}
                        {showItDiagnostics && activeTab === 'network' && (
                            <div className='activity-tab-panel'>
                                {itDiagnosticsLoading ? (
                                    <div className='no-activity-msg'>Loading network diagnostics...</div>
                                ) : (
                                    <NetworkDiagnosticsTab
                                        network={itDiagnostics?.network}
                                        endpoint={itDiagnostics?.endpoint}
                                        actionItemId={actionItem?.actionItem?.id}
                                        actionItemPropertyId={actionItem?.actionItem?.propertyID}
                                        onRefresh={() => {
                                            setItDiagnostics(null);
                                            setItDiagnosticsLoading(true);
                                            apiFetch(`/api/ITDiagnostics/action-item/${actionItem.actionItem.id}`)
                                                .then(r => r.json())
                                                .then(d => { if (d.success) setItDiagnostics(d); })
                                                .catch(e => Sentry.captureException(e))
                                                .finally(() => setItDiagnosticsLoading(false));
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        {/* ── Automations tab ───────────────────────────────────── */}
                        {/* Shows all workflow automations applicable to this action item's
                            template, with last-run info, ETA, and per-item enable/disable
                            toggles.  isReadOnly users can view but not change overrides. */}
                        {(actionItem?.actionItem?.templateKey || actionItem?.actionItem?.boardId) && activeTab === 'automations' && (
                            <div className='activity-tab-panel'>
                                <ActionItemAutomationsTab
                                    actionItemId={actionItem.actionItem.id}
                                    isReadOnly={user?.ReadOnly || user?.readOnly || false}
                                    onCountChange={setAutomationsCount}
                                />
                            </div>
                        )}

                        {/* ── Tasks tab ─────────────────────────────── */}
                        {activeTab === 'tasks' && (
                            <ActionItemTasksTab
                                taskGroups={templateTasks}
                                taskValues={taskValues}
                                onTaskChange={handleTaskChange}
                                readOnly={status === 'Closed' || status === 'Cancelled'}
                            />
                        )}
                    </div>
                </div>

    {/* ============================================================ */}
 {/* RIGHT COLUMN: Sidebar with Action Item Details */}
            {/* ============================================================ */}
        
        <aside className='action-item-sidebar'>
<div className='sidebar-section'>
     <div ref={sidebarRef}>
  
            {/* ================================================ */}
  {/* Assigned User Section */}
       {/* ================================================ */}
    
         <div
className='sidebar-label-row'
             onClick={(e) => {
     e.stopPropagation();
    setShowUserEditor(s => !s);
        if (!showUserEditor) {
      setUserSearch(getUserDisplay(assignedUser));
          setTimeout(() => setShowUserSuggestions(true), 50);
              }
     }}
       style={{ cursor: 'pointer' }}
    >
         <h4><span className='sidebar-label'>Assigned User:</span></h4>
      <span className='sidebar-gear' title='Assign user'>
      <FaCog size={20} />
   </span>
        </div>

            {/* Display Current Assignment */}
       {!showUserEditor && (
             <div className='sidebar-value'>
        {assignedUser ? (
        <span className='sidebar-assigned-user'>{getUserDisplay(assignedUser)}</span>
        ) : (
<>
   <span className='sidebar-unassigned'>Unassigned</span>
   {currentUserId && (
               <button className='assign-yourself-btn' onClick={handleAssignYourself}>
      Assign yourself
           </button>
        )}
         </>
          )}
       </div>
         )}

       {/* User Search & Assignment Editor */}
      {showUserEditor && (
         <div ref={userAssignRef} style={{ position: 'relative', marginBottom: 8 }}>
 <input
   type='text'
      value={userSearch}
          onChange={e => {
       setUserSearch(e.target.value);
            setShowUserSuggestions(true);
   }}
     onFocus={() => setShowUserSuggestions(true)}
        placeholder='Search users...'
   className='sidebar-user-search'
    style={{ width: '100%', padding: '6px 8px', fontSize: '0.75rem' }}
 />
 
              {/* User Suggestions Dropdown */}
             {showUserSuggestions && (
  <div style={{
      position: 'absolute',
            top: '100%',
           left: 0,
          right: 0,
     background: '#fff',
border: '1px solid #ccc',
                  borderRadius: 4,
       zIndex: 10,
             maxHeight: 220,
         overflowY: 'auto',
     boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                 }}>
   {filteredUsers.slice(0, 40).map(u => {
          const idVal = u.userID ?? u.id;
         const label = getUserDisplay(u);
       const isSelf = idVal === currentUserId;
        
      return (
     <div
           key={idVal}
          onClick={() => assignUser(idVal)}
      style={{
          padding: '6px 10px',
         fontSize: '0.7rem',
     cursor: 'pointer',
      display: 'flex',
           justifyContent: 'space-between',
 alignItems: 'center'
       }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
             onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
     >
         <span style={{
            whiteSpace: 'nowrap',
   overflow: 'hidden',
                textOverflow: 'ellipsis',
      maxWidth: '70%'
            }}>
               {label}
    </span>
     {isSelf && (
        <span style={{
      fontSize: '0.55rem',
           background: '#6366f1',
           color: '#fff',
           padding: '2px 4px',
              borderRadius: 3
}}>
            You
            </span>
      )}
     </div>
        );
            })}
           
   {filteredUsers.length === 0 && (
    <div style={{
       padding: '8px 10px',
 fontSize: '0.65rem',
          color: '#92400e',
       background: '#fff7ed'
     }}>
        No matching users.
           </div>
      )}
      </div>
           )}
 </div>
      )}

       {/* ================================================ */}
       {/* Requester Section (IT tickets; logs on user's behalf) */}
       {/* ================================================ */}
       <div
           className='sidebar-label-row space-above'
           onClick={(e) => {
               e.stopPropagation();
               setShowRequesterEditor(s => !s);
               if (!showRequesterEditor) {
                   setRequesterSearch(getUserDisplay(requesterUser));
                   setTimeout(() => setShowRequesterSuggestions(true), 50);
               }
           }}
           style={{ cursor: 'pointer' }}
       >
           <h4><span className='sidebar-label'>Requester:</span></h4>
           <span className='sidebar-gear' title='Change requester (person ticket is for)'>
               <FaCog size={20} />
           </span>
       </div>

       {!showRequesterEditor && (
           <div className='sidebar-value'>
               {requesterUser ? (
                   <span className='sidebar-assigned-user'>{getUserDisplay(requesterUser)}</span>
               ) : tenantContactEmail ? (
                   <span className='sidebar-assigned-user' title='External requester (email only)'>{tenantContactEmail}</span>
               ) : (
                   <span className='sidebar-unassigned'>No requester</span>
               )}
           </div>
       )}

       {showRequesterEditor && (
           <div style={{ position: 'relative', marginBottom: 8 }}>
               <input
                   type='text'
                   value={requesterSearch}
                   onChange={e => { setRequesterSearch(e.target.value); setShowRequesterSuggestions(true); }}
                   onFocus={() => setShowRequesterSuggestions(true)}
                   placeholder='Search users...'
                   className='sidebar-user-search'
                   disabled={isSavingRequester}
                   style={{ width: '100%', padding: '6px 8px', fontSize: '0.75rem' }}
               />
               {showRequesterSuggestions && (
                   <div style={{
                       position: 'absolute', top: '100%', left: 0, right: 0,
                       background: '#fff', border: '1px solid #ccc', borderRadius: 4,
                       zIndex: 10, maxHeight: 220, overflowY: 'auto',
                       boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                   }}>
                       {filteredRequesterUsers.slice(0, 40).map(u => {
                           const idVal = u.userID ?? u.id;
                           const label = getUserDisplay(u);
                           const isSelf = idVal === currentUserId;
                           return (
                               <div
                                   key={idVal}
                                   onClick={() => assignRequester(idVal)}
                                   style={{
                                       padding: '6px 10px', fontSize: '0.7rem', cursor: 'pointer',
                                       display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                                   }}
                                   onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                                   onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                               >
                                   <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }}>
                                       {label}
                                   </span>
                                   {isSelf && (
                                       <span style={{ fontSize: '0.55rem', background: '#6366f1', color: '#fff', padding: '2px 4px', borderRadius: 3 }}>
                                           You
                                       </span>
                                   )}
                               </div>
                           );
                       })}
                       {filteredRequesterUsers.length === 0 && (
                           <div style={{ padding: '8px 10px', fontSize: '0.65rem', color: '#92400e', background: '#fff7ed' }}>
                               No matching users.
                           </div>
                       )}
                   </div>
               )}
           </div>
       )}

       {/* ================================================ */}
       {/* Assigned Department Section */}
        {/* ================================================ */}
        
        <div className='sidebar-label-row space-above'>
            <h4><span className='sidebar-label'>Assigned Department:</span></h4>
            <span
                className='sidebar-gear'
                title='Assign to department'
                onClick={(e) => {
                    e.stopPropagation();
                    setShowDepartmentEditor(s => !s);
                }}
                style={{ cursor: 'pointer' }}
            >
                <FaBuilding size={18} />
            </span>
        </div>

        {/* Display Current Department Assignment */}
        {!showDepartmentEditor && (
            <div className='sidebar-value'>
                {assignedDepartment ? (
                    <span className='sidebar-assigned-user'>
                        <FaUsers size={14} style={{ marginRight: '6px', color: assignedDepartment.color || '#718096' }} />
                        {assignedDepartment.departmentName}
                    </span>
                ) : (
                    <span className='sidebar-unassigned'>No department</span>
                )}
            </div>
        )}

        {/* Department Selection Dropdown */}
        {showDepartmentEditor && (
            <div className='department-editor-wrapper'>
                <select
                    className='sidebar-department-select'
                    value={assignedDepartment?.departmentID || ''}
                    onChange={(e) => {
                        const deptId = e.target.value ? parseInt(e.target.value, 10) : null;
                        if (deptId) {
                            assignDepartment(deptId);
                        } else {
                            // Clear department assignment - use 0 instead of null for API compatibility
                            assignDepartment(0);
                        }
                    }}
                >
                    <option value=''>-- No Department --</option>
                    {departments.map(dept => (
                        <option key={dept.departmentID} value={dept.departmentID}>
                            {dept.departmentName}
                        </option>
                    ))}
                </select>
                <p className='department-help-text'>
                    Department assignment works alongside user and property assignments.
                </p>
            </div>
        )}

        {/* ================================================ */}
        {/* Assigned Property Section */}
        {/* ================================================ */}

        <div className='sidebar-label-row space-above'>
            <h4><span className='sidebar-label'>Assigned Property:</span></h4>
            <span
                className='sidebar-gear'
                title='Assign to property'
                onClick={(e) => {
                    e.stopPropagation();
                    setShowPropertyAssignEditor(s => !s);
                }}
                style={{ cursor: 'pointer' }}
            >
                <FaBuilding size={18} />
            </span>
        </div>

        {/* Display Current Property Assignment */}
        {!showPropertyAssignEditor && (
            <div className='sidebar-value'>
                {assignedProperty ? (
                    <span className='sidebar-assigned-user'>
                        {assignedProperty.propertyName}
                    </span>
                ) : (
                    <span className='sidebar-unassigned'>No property</span>
                )}
            </div>
        )}

        {/* Property Selection Dropdown */}
        {showPropertyAssignEditor && (
            <div className='department-editor-wrapper'>
                <select
                    className='sidebar-department-select'
                    value={assignedProperty?.propertyID || ''}
                    onChange={(e) => {
                        const propId = e.target.value ? parseInt(e.target.value, 10) : null;
                        if (propId) {
                            assignProperty(propId);
                        } else {
                            assignProperty(0);
                        }
                    }}
                >
                    <option value=''>-- No Property --</option>
                    {properties.map(prop => (
                        <option key={prop.propertyID} value={prop.propertyID}>
                            {prop.propertyName}
                        </option>
                    ))}
                </select>
                <p className='department-help-text'>
                    Users marked as &quot;Responsible&quot; for this property will see this action item.
                </p>
            </div>
        )}

        {/* ================================================ */}
        {/* Status, Priority, and Escalation Section */}
        {/* ================================================ */}
        <StatusPrioritySection
            status={status}
            onStatusChange={handleStatusChange}
            showStatusSelect={showStatusSelect}
            onToggleStatusSelect={() => setShowStatusSelect(s => !s)}
            priority={priority}
            onPriorityChange={handlePriorityChange}
            showPrioritySelect={showPrioritySelect}
            onTogglePrioritySelect={() => setShowPrioritySelect(s => !s)}
            isEscalated={actionItem?.actionItem?.isEscalated}
            escalatedToUserID={actionItem?.actionItem?.escalatedToUserID}
            escalationReason={actionItem?.actionItem?.escalationReason}
            onEscalate={() => setShowEscalateModal(true)}
            onDeEscalate={deEscalateActionItem}
            assignedUser={assignedUser}
            getUserName={getUserName}
        />

        {/* ================================================ */}
        {/* Meta Information (Creator, Dates) */}
        {/* ================================================ */}

        {/* ================================================ */}
        {/* Board Assignment Section */}
        {/* ================================================ */}
        
        <div className='sidebar-label-row sidebar-label-static space-above'>
            <h4><span className='sidebar-label'>Board:</span></h4>
        </div>
        <ActionItemBoardSelector
            currentBoardId={actionItem.actionItem.boardId}
            onBoardChange={async (newBoardId) => {
                try {
                    const response = await apiFetch(`/api/ActionItems/${id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            boardId: newBoardId,
                            boardIdProvided: true,
                            userId: user.userID
                        })
                    });
                    
                    if (response.ok) {
                        toast.success('Board updated successfully');
                        // Update local state — this also resets selectedBoardId in the selector
                        setActionItem(prev => ({
                            ...prev,
                            actionItem: {
                                ...prev.actionItem,
                                boardId: newBoardId
                            }
                        }));
                        return true; // Signal success so the selector keeps the new selection
                    } else {
                        const errorData = await response.json().catch(() => null);
                        toast.error(errorData?.message || 'Failed to update board');
                        return false; // Signal failure so the selector reverts to previous board
                    }
                } catch (error) {
                    Sentry.captureException(error);
                    console.error('Error updating board:', error);
                    toast.error('Failed to update board');
                    return false; // Signal failure so the selector reverts to previous board
                }
            }}
            disabled={false}
        />

        {/* ================================================ */}
        {/* Metadata Section */}
        {/* ================================================ */}

        {/* Created By */}
        <div className='sidebar-label-row sidebar-label-static space-above'>
            <h4><span className='sidebar-label'>Created By:</span></h4>
        </div>
        <div className='sidebar-value sidebar-creator'>
            <span>{getUserName(actionItem.actionItem.userID)}</span>
        </div>

        {/* Created Date - formatted with date and time, displayed in GMT+2 (Africa/Johannesburg) */}
        <div className='sidebar-label-row sidebar-label-static space-above'>
            <h4><span className='sidebar-label'>Created:</span></h4>
        </div>
        <div className='sidebar-value sidebar-date'>
            <span>{actionItem.actionItem.createdDate
                ? new Date(actionItem.actionItem.createdDate).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Africa/Johannesburg'
                })
                : 'N/A'}</span>
        </div>

        {/* Last Updated - show if different from created */}
        <div className='sidebar-label-row sidebar-label-static space-above'>
            <h4><span className='sidebar-label'>Last Updated:</span></h4>
        </div>
        <div className='sidebar-value sidebar-date'>
            <span>{actionItem.logs && actionItem.logs.length > 0
                ? new Date(actionItem.logs[0].changedAt).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Africa/Johannesburg'
                })
                : 'N/A'}</span>
        </div>

        {/* ================================================ */}
        {/* Progress Bar Section                              */}
        {/* Visual indicator of how far along this item is.  */}
        {/* Click the percentage to enter an edit mode with  */}
        {/* a slider and numeric input.                      */}
        {/* ================================================ */}
        <div className='sidebar-label-row space-above'>
            <h4><span className='sidebar-label'>Progress:</span></h4>
            <span
                className='sidebar-gear'
                title='Edit progress'
                onClick={() => setIsEditingProgress(s => !s)}
                style={{ cursor: 'pointer' }}
            >
                <FaEdit size={16} />
            </span>
        </div>
        <div className='progress-bar-section'>
            <div className='progress-bar-container'>
                {/* Filled portion — width driven by the current progress value */}
                <div
                    className='progress-bar-fill'
                    style={{ width: `${progressValue}%` }}
                    title={`${progressValue}% complete`}
                />
            </div>
            <span className='progress-bar-label'>{progressValue}%</span>
        </div>
        {isEditingProgress && (
            <div className='progress-edit-row'>
                <input
                    type='range'
                    min={0}
                    max={100}
                    step={5}
                    value={progressValue}
                    onChange={(e) => setProgressValue(Number(e.target.value))}
                    className='progress-slider'
                />
                <input
                    type='number'
                    min={0}
                    max={100}
                    value={progressValue}
                    onChange={(e) => setProgressValue(Math.max(0, Math.min(100, Number(e.target.value))))}
                    className='progress-number-input'
                />
                <button
                    className='btn-save-progress'
                    onClick={() => handleSaveProgress(progressValue)}
                    disabled={isSavingProgress}
                    title='Save progress'
                >
                    {isSavingProgress ? '…' : <FaCheck size={12} />}
                </button>
                <button
                    className='btn-cancel-progress'
                    onClick={() => {
                        setIsEditingProgress(false);
                        setProgressValue(actionItem?.actionItem?.progressPercent ?? 0);
                    }}
                    disabled={isSavingProgress}
                    title='Cancel'
                >
                    <FaTimes size={12} />
                </button>
            </div>
        )}

        {/* ================================================ */}
        {/* SLA Tracking Widget (IT tickets only)             */}
        {/* Shows response/resolution deadlines and status   */}
        {/* ================================================ */}
        {actionItem?.actionItem?.actionItemsType &&
            isItTicketType(actionItem.actionItem.actionItemsType) && (
            <SlaTrackingWidget actionItemId={actionItem.actionItem.id} />
        )}

        {/* ================================================ */}
        {/* Remote Connect (Splashtop — IT tickets only)      */}
        {/* Launches Splashtop to the requester's last device */}
        {/* ================================================ */}
        {remoteConnectConfig?.enabled && actionItem?.actionItem?.actionItemsType &&
            isItTicketType(actionItem.actionItem.actionItemsType) && (
            <div className='sidebar-section remote-connect-section'>
                <h4 className='sidebar-label'>Remote Support</h4>
                {remoteDeviceInfo?.lastDeviceHostname ? (
                    <>
                        <button
                            className='btn-remote-connect'
                            onClick={handleRemoteConnect}
                            title={`Connect to ${remoteDeviceInfo.lastDeviceHostname}${remoteDeviceInfo.lastDeviceIpAddress ? ` (${remoteDeviceInfo.lastDeviceIpAddress})` : ''}`}
                        >
                            <FaDesktop /> Remote Connect
                        </button>
                        <div className='remote-device-info'>
                            <span className='remote-device-hostname'>{remoteDeviceInfo.lastDeviceHostname}</span>
                            {remoteDeviceInfo.lastDeviceIpAddress && (
                                <span className='remote-device-ip'>{remoteDeviceInfo.lastDeviceIpAddress}</span>
                            )}
                            {remoteDeviceInfo.lastDeviceUpdatedAt && (
                                <span className='remote-device-updated'>
                                    Last seen: {new Date(remoteDeviceInfo.lastDeviceUpdatedAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </>
                ) : (
                    <span className='remote-no-device'>No device info available for this user</span>
                )}
            </div>
        )}

        {/* ================================================ */}
        {/* Due Dates Section                                 */}
        {/* Displays deadline with colour-coded urgency badge */}
        {/* and allows the due date to be changed inline.    */}
        {/* ================================================ */}
        {(() => {
            const ai = actionItem?.actionItem;
            const dueDateValue = ai?.dueDate || ai?.expectedCompletionDate;
            const startDateValue = ai?.startDate;

            // Determine urgency of the due date for visual cues
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const due = dueDateValue ? new Date(dueDateValue) : null;
            const daysUntilDue = due ? Math.ceil((due - today) / (1000 * 60 * 60 * 24)) : null;

            const dueDateClass = due
                ? daysUntilDue < 0
                    ? 'due-date-chip overdue'
                    : daysUntilDue <= 3
                        ? 'due-date-chip due-soon'
                        : 'due-date-chip'
                : '';

            const dueDateLabel = due
                ? daysUntilDue < 0
                    ? `Overdue by ${Math.abs(daysUntilDue)} day(s)`
                    : daysUntilDue === 0
                        ? 'Due today'
                        : daysUntilDue === 1
                            ? 'Due tomorrow'
                            : `Due in ${daysUntilDue} day(s)`
                : null;

            return (
                <>
                    {/* Due date with urgency badge */}
                    <div className='sidebar-label-row sidebar-label-static space-above'>
                        <h4><span className='sidebar-label'>Due Date:</span></h4>
                    </div>
                    <div className='sidebar-value'>
                        {due ? (
                            <span className={dueDateClass}>
                                {due.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {dueDateLabel && <em className='due-urgency-label'>{dueDateLabel}</em>}
                            </span>
                        ) : (
                            <span className='sidebar-unassigned'>Not set</span>
                        )}
                    </div>
                    {/* Inline date editor */}
                    <input
                        type='date'
                        className='sidebar-date-input'
                        defaultValue={due ? due.toISOString().split('T')[0] : ''}
                        onBlur={(e) => {
                            if (e.target.value) {
                                handleSaveDueDate(e.target.value);
                            }
                        }}
                        title='Set or change the due date'
                    />

                    {/* Start date — read-only display */}
                    {startDateValue && (
                        <>
                            <div className='sidebar-label-row sidebar-label-static space-above'>
                                <h4><span className='sidebar-label'>Start Date:</span></h4>
                            </div>
                            <div className='sidebar-value sidebar-date'>
                                <span>
                                    {new Date(startDateValue).toLocaleDateString('en-GB', {
                                        day: '2-digit', month: 'short', year: 'numeric'
                                    })}
                                </span>
                            </div>
                        </>
                    )}
                </>
            );
        })()}

        {/* ================================================ */}
        {/* Tags Widget                                       */}
        {/* Tags are stored as a comma-separated string.     */}
        {/* Each tag is shown as a removable chip.           */}
        {/* New tags are added via a small text input.       */}
        {/* ================================================ */}
        <div className='sidebar-label-row sidebar-label-static space-above'>
            <h4><span className='sidebar-label'>Tags:</span></h4>
        </div>
        <div className='tags-widget'>
            {/* Render each tag as a removable chip */}
            {parsedTags.length > 0 ? (
                <div className='tags-chip-list'>
                    {parsedTags.map(tag => (
                        <span key={tag} className='tag-chip'>
                            {tag}
                            <button
                                className='tag-chip-remove'
                                onClick={() => handleRemoveTag(tag)}
                                disabled={isSavingTags}
                                title={`Remove tag "${tag}"`}
                                aria-label={`Remove tag ${tag}`}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <span className='sidebar-unassigned'>No tags</span>
            )}
            {/* Add new tag input — submits on Enter or blur */}
            <div className='tag-add-row'>
                <input
                    type='text'
                    className='tag-add-input'
                    value={newTagInput}
                    placeholder='Add tag…'
                    onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTag();
                        }
                    }}
                    disabled={isSavingTags}
                    maxLength={50}
                />
                <button
                    className='tag-add-btn'
                    onClick={handleAddTag}
                    disabled={isSavingTags || !newTagInput.trim()}
                    title='Add tag'
                >
                    +
                </button>
            </div>
        </div>

        {/* ================================================ */}
        {/* Static Information Fields */}
        {/* ================================================ */}

        {/* Description is displayed in the main Action Item Details panel above,
            so it is intentionally omitted here to avoid duplication. */}

        {/* Property */}
                <div className='sidebar-label-row sidebar-label-static space-above'>
                    <h4><span className='sidebar-label'>Property:</span></h4>
                    {canAccessActionItemsPage && (
                        <span
                            className='sidebar-gear'
                            title='Edit property assignment'
                            onClick={() => {
                                setEditingPropertyId(actionItem?.actionItem?.propertyID ?? '');
                                setShowPropertyEditor(prev => !prev);
                            }}
                        >
                            <FaEdit size={16} />
                        </span>
                    )}
                </div>
                {showPropertyEditor && canAccessActionItemsPage ? (
                    <div className='sidebar-property-editor'>
                        <select
                            value={editingPropertyId ?? ''}
                            onChange={(e) => setEditingPropertyId(e.target.value)}
                            className='sidebar-property-select'
                        >
                            <option value="">-- Select Property --</option>
                            {properties.map(p => (
                                <option key={p.propertyID} value={p.propertyID}>
                                    {p.propertyName}
                                </option>
                            ))}
                        </select>
                        <div className='sidebar-property-editor-actions'>
                            <button
                                className='btn-save-property'
                                onClick={() => handlePropertyUpdate(editingPropertyId)}
                                disabled={isSavingProperty}
                                title='Save property change'
                            >
                                {isSavingProperty ? '...' : <FaCheck size={12} />}
                            </button>
                            <button
                                className='btn-cancel-property'
                                onClick={() => setShowPropertyEditor(false)}
                                disabled={isSavingProperty}
                                title='Cancel'
                            >
                                <FaTimes size={12} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className='sidebar-value sidebar-property'>
                        <span>{propertyName}</span>
                    </div>
                )}

  {/* Shop Number */}
      <div className='sidebar-label-row sidebar-label-static space-above'>
     <h4><span className='sidebar-label'>Shop Number:</span></h4>
           </div>
     <div className='sidebar-value sidebar-property'>
      <span>{actionItem.actionItem.shopNumber}</span>
           </div>

      {/* Initial Comment */}
  <div className='sidebar-label-row sidebar-label-static space-above'>
       <h4><span className='sidebar-label'>Initial Comment:</span></h4>
   </div>
            <div className='sidebar-value sidebar-comment'>
      <span>{actionItem.actionItem.comments}</span>
       </div>

      {/* ================================================ */}
   {/* Tenant Details Section - Only show if there's tenant data */}
       {/* ================================================ */}

      {(actionItem.actionItem.tenant || actionItem.actionItem.tenantResponse || 
        actionItem.actionItem.tenantContactName || actionItem.actionItem.tenantContactNumber || actionItem.actionItem.tenantContactEmail) && (
          <>
              <div className='tenant-details'>
 <h2><span className='sidebar-label tenant-details'>Tenant Details</span></h2>
   </div>

      {/* Tenant Name - only show if has value */}
      {actionItem.actionItem.tenant && (
          <>
      <div className='sidebar-label-row sidebar-label-static'>
 <h4><span className='sidebar-label'>Tenant:</span></h4>
           </div>
         <div className='sidebar-value sidebar-comment'>
             <span>{actionItem.actionItem.tenant}</span>
             </div>
          </>
      )}

       {/* Tenant Response - only show if has value */}
      {actionItem.actionItem.tenantResponse && (
          <>
      <div className='sidebar-label-row sidebar-label-static'>
          <h4><span className='sidebar-label'>Tenant Response:</span></h4>
        </div>
            <div className='sidebar-value sidebar-comment'>
     <span>{actionItem.actionItem.tenantResponse}</span>
        </div>
          </>
      )}

         {/* ================================================ */}
 {/* Editable Contact Fields */}
     {/* ================================================ */}

              {/* Contact Name */}
           <div className='sidebar-label-row sidebar-label-static'>
     <h4><span className='sidebar-label'>Contact Name:</span></h4>
    </div>
   <input
              type='text'
     className='sidebar-contact-input'
             value={tenantContactName}
       onChange={e => setTenantContactName(e.target.value)}
   onBlur={e => {
     if (isValidName(e.target.value) || e.target.value === '') {
           saveContactField('tenantContactName', e.target.value);
           lookupAndPopulateContact('name', e.target.value);
    } else {
         toast.error('Name must only contain letters, spaces, hyphens, and accented characters');
   setTenantContactName(actionItem.actionItem.tenantContactName ?? '');
     }
      }}
   placeholder='Enter contact name'
         />

       {/* Contact Number */}
      <div className='sidebar-label-row sidebar-label-static space-above'>
    <h4><span className='sidebar-label'>Contact Number:</span></h4>
                </div>
          <input
       type='text'
          className='sidebar-contact-input'
   value={tenantContactNumber}
    onChange={e => setTenantContactNumber(e.target.value)}
           onBlur={e => {
               if (isValidNumber(e.target.value) || e.target.value === '') {
        saveContactField('tenantContactNumber', e.target.value);
        lookupAndPopulateContact('phone', e.target.value);
         } else {
                toast.error('Number must only contain digits');
                setTenantContactNumber(actionItem.actionItem.tenantContactNumber ?? '');
       }
             }}
    placeholder='Enter contact number'
         />

        {/* Contact Email */}
      <div className='sidebar-label-row sidebar-label-static space-above'>
  <h4><span className='sidebar-label'>Contact Email:</span></h4>
       </div>
       <input
       type='email'
   className='sidebar-contact-input'
       value={tenantContactEmail}
   onChange={e => setTenantContactEmail(e.target.value)}
              onBlur={e => {
                if (isValidEmail(e.target.value)) {
              saveContactField('tenantContactEmail', e.target.value);
              lookupAndPopulateContact('email', e.target.value);
          } else {
               toast.error('Invalid email address');
           setTenantContactEmail(actionItem.actionItem.tenantContactEmail ?? '');
                  }
            }}
            placeholder='Enter contact email'
       />
          </>
      )}
     </div>
      </div>
      </aside>

    {/* Toast Notifications */}
{/* Escalate Modal */}
    {showEscalateModal && (
        <div className='escalate-modal-overlay' onClick={() => setShowEscalateModal(false)}>
            <div className='escalate-modal' onClick={(e) => e.stopPropagation()}>
                <div className='escalate-modal-header'>
                    <h3>Escalate Action Item</h3>
                    <button 
                        className='escalate-modal-close'
                        onClick={() => setShowEscalateModal(false)}
                    >
                        &times;
                    </button>
                </div>
                <div className='escalate-modal-body'>
                    <p>
                        This will escalate the action item to the assigned user's manager.
                        {assignedUser && (
                            <span> Currently assigned to: <strong>{getUserDisplay(assignedUser)}</strong></span>
                        )}
                    </p>
                    <label>
                        Reason for escalation:
                        <textarea
                            value={escalationReason}
                            onChange={(e) => setEscalationReason(e.target.value)}
                            placeholder='Enter reason for escalation...'
                            rows={3}
                        />
                    </label>
                </div>
                <div className='escalate-modal-footer'>
                    <button
                        className='btn-cancel'
                        onClick={() => {
                            setShowEscalateModal(false);
                            setEscalationReason('');
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className='btn-escalate'
                        onClick={() => escalateActionItem(escalationReason)}
                        disabled={isEscalating}
                    >
                        {isEscalating ? 'Escalating...' : 'Escalate'}
                    </button>
                </div>
            </div>
        </div>
    )}

    {/* Escalate Comment Modal - Creates a new action item from a comment */}
    {showEscalateCommentModal && selectedCommentForEscalation && (
        <div className='escalate-modal-overlay' onClick={() => {
            setShowEscalateCommentModal(false);
            setSelectedCommentForEscalation(null);
        }}>
            <div className='escalate-modal' onClick={(e) => e.stopPropagation()}>
                <div className='escalate-modal-header'>
                    <h3>Escalate Comment to Action Item</h3>
                    <button 
                        className='escalate-modal-close'
                        onClick={() => {
                            setShowEscalateCommentModal(false);
                            setSelectedCommentForEscalation(null);
                        }}
                    >
                        &times;
                    </button>
                </div>
                <div className='escalate-modal-body'>
                    <p>
                        This will create a new Action Item from the selected comment.
                        The new item will be linked to this action item.
                    </p>
                    <div className='comment-preview'>
                        <strong>Comment to escalate:</strong>
                        <blockquote>
                            {selectedCommentForEscalation.comment}
                        </blockquote>
                        <small>
                            By {getUserName(selectedCommentForEscalation.changedByUserId)} on {formatCommentDateTime(selectedCommentForEscalation.changedAt)}
                        </small>
                    </div>
                </div>
                <div className='escalate-modal-footer'>
                    <button
                        className='btn-cancel'
                        onClick={() => {
                            setShowEscalateCommentModal(false);
                            setSelectedCommentForEscalation(null);
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className='btn-escalate'
                        onClick={() => escalateCommentToActionItem(selectedCommentForEscalation)}
                    >
                        <FaLevelUpAlt size={14} /> Create Action Item
                    </button>
                </div>
            </div>
        </div>
    )}

    {/* PDF Annotation Modal */}
    {showPdfAnnotationModal && selectedPdfAttachment && (
        <PdfAnnotationModal
            isOpen={showPdfAnnotationModal}
            onClose={() => {
                setShowPdfAnnotationModal(false);
                setSelectedPdfAttachment(null);
            }}
            attachment={selectedPdfAttachment}
            actionItemId={parseInt(id)}
        />
    )}
</div>
            {/* ===================================================== */}
            {/* Admin Delete Confirmation Modal */}
            {/* ===================================================== */}
            {showDeleteModal && (
                <div className="modal-backdrop" onClick={() => !isDeletingItem && setShowDeleteModal(false)}>
                    <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h2><FaTrashAlt style={{ color: '#e53e3e', marginRight: 8 }} />Delete Action Item</h2>
                        <p>
                            Are you sure you want to <strong>permanently delete</strong> this action item?
                            This will remove all related logs, attachments, comments, and collaborator data.
                            <br /><strong>This action cannot be undone.</strong>
                        </p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isDeletingItem}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-confirm-delete"
                                onClick={handleDeleteActionItem}
                                disabled={isDeletingItem}
                            >
                                {isDeletingItem ? 'Deleting...' : 'Yes, Delete Permanently'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CSAT Survey Modal — shown for IT ticket requesters when ticket is solved */}
            {actionItem?.actionItem?.actionItemsType &&
                isItTicketType(actionItem.actionItem.actionItemsType) &&
                (status === 'Solved' || status === 'Closed') && (
                <CsatSurveyModal
                    actionItemId={actionItem.actionItem.id}
                    ticketTitle={actionItem.actionItem.ticketReference
                        ? `${actionItem.actionItem.ticketReference} — ${actionItem.actionItem.title}`
                        : actionItem.actionItem.title}
                />
            )}
        </div>
  );
};

export default ActionItemDetailPage;
